/**
 * Kit Builder JavaScript
 * Converts Svelte calculator logic to vanilla JavaScript for Liquid theme
 */

// Patch getFocusableElements to handle null containers
if (typeof getFocusableElements !== 'undefined') {
  const originalGetFocusableElements = getFocusableElements;
  globalThis.getFocusableElements = function (container) {
    if (!container) {
      return [];
    }
    return originalGetFocusableElements(container);
  };
}

// Patch CartNotification to handle missing sections gracefully
// This will be applied after cart-notification.js loads
setTimeout(() => {
  const CartNotificationElement = customElements.get('cart-notification');
  if (CartNotificationElement) {
    const originalRenderContents = CartNotificationElement.prototype.renderContents;
    CartNotificationElement.prototype.renderContents = function (parsedState) {
      // Verify all required sections exist before rendering
      const requiredSections = this.getSectionsToRender().map((s) => s.id);
      const missingSections = requiredSections.filter((id) => !parsedState.sections?.[id]);

      if (missingSections.length > 0) {
        return;
      }

      return originalRenderContents.call(this, parsedState);
    };
  }
}, 100);

class KitBuilder {
  constructor(sectionId, config) {
    this.sectionId = sectionId;
    this.initializeConfigProperties(config);
    this.initializeState();
    this.initializeCoverageRates(config);
    this.bindMethods();
  }

  /**
   * Initialize configuration properties from config object
   */
  initializeConfigProperties(config) {
    this.products = config.products || [];
    // Handle discountRate properly - 0 is a valid value, so check for undefined/null
    this.discountRate = config.discountRate !== undefined && config.discountRate !== null ? config.discountRate : 0.1;
    this.storeDomain = config.storeDomain || '';
    this.calculatorId = config.calculatorId || '';
    this.inputMode = config.inputMode || 'length';

    // Initialize dimension defaults and limits
    this.defaultLength = this.parseConfigNumber(config.defaultLength, 6);
    this.defaultWidth = this.parseConfigNumber(config.defaultWidth, 2.5);
    this.defaultArea = this.parseConfigNumber(config.defaultArea, 15);
    this.maxLength = this.parseConfigNumber(config.maxLength, 30);
    this.maxWidth = this.parseConfigNumber(config.maxWidth, 10);
    this.maxArea = this.parseConfigNumber(config.maxArea, 300);
    this.minLength = this.parseConfigNumber(config.minLength, 0.5);
    this.minWidth = this.parseConfigNumber(config.minWidth, 0.5);
    this.minArea = this.parseConfigNumber(config.minArea, 0.5);

    // Initialize toggle settings
    this.showRawMetalToggle = config.showRawMetalToggle ?? true;
    this.showEtchPrimer = config.showEtchPrimer ?? false;
    this.showFullCoverToggle = config.showFullCoverToggle ?? false;
    this.showConcreteRoofToggle = config.showConcreteRoofToggle ?? false;
    this.showPartialAreaToggle = config.showPartialAreaToggle ?? false;
    this.partialAreaToggleLabel = config.partialAreaToggleLabel || 'Partial area coverage?';
    this.partialAreaTooltipText = config.partialAreaTooltipText || '';
    this.partialAreaSliderLabel = config.partialAreaSliderLabel || 'Coverage: 0–100% of roof';
    this.partialAreaSliderDefault =
      config.partialAreaSliderDefault !== undefined && config.partialAreaSliderDefault !== null
        ? Math.min(100, Math.max(0, Number(config.partialAreaSliderDefault)))
        : 20;
    this.partialAreaProductHandle = config.partialAreaProductHandle || null;
    this.showRecommendedColorProductHandle = config.showRecommendedColorProductHandle || null;
    this.fullCoverToggleLabel = config.fullCoverToggleLabel || 'Full cover?';
    this.fullCoverTooltipText = config.fullCoverTooltipText || 'Full coverage vs seams only';

    // Initialize bonus kit configuration
    this.bonusKitAreaThreshold = config.bonusKitAreaThreshold ?? 5;
    this.bonusBrushProductHandle = config.bonusBrushProductHandle || null;
    this.bonusBrushRollerProductHandle = config.bonusBrushRollerProductHandle || null;
    this.bonusBrushVariantId = config.bonusBrushVariantId || null;
    this.bonusBrushRollerVariantId = config.bonusBrushRollerVariantId || null;
    this.bonusBrushText = config.bonusBrushText || 'Brush Kit';
    this.bonusBrushPrice = config.bonusBrushPrice || '14.99';
    this.bonusBrushRollerText = config.bonusBrushRollerText || 'Brush + Roller Kit';
    this.bonusBrushRollerPrice = config.bonusBrushRollerPrice || '39.99';
  }

  /**
   * Parse a config number value with fallback to default
   */
  parseConfigNumber(value, defaultValue) {
    return value !== undefined && value !== null ? Number.parseFloat(value) : defaultValue;
  }

  /**
   * Initialize calculator state
   */
  initializeState() {
    this.length = this.defaultLength;
    this.width = this.defaultWidth;
    this.area = this.defaultArea;
    // If showEtchPrimer is true, default to bare-metal (etch primer visible)
    this.roofType = this.showEtchPrimer ? 'bare-metal' : 'painted-roof';
    this.fullCover = false; // Default to seams only (not full coverage)
    this.concreteRoof = false; // Default to off
    this.partialAreaEnabled = false; // Partial area toggle off by default
    this.partialAreaPercent = this.partialAreaSliderDefault; // 0–100% of roof for partial area product
    this.selectedColors = {};
    this.items = [];
    this.breakdowns = [];
    this.totalItems = 0;
    this.originalPrice = 0;
    this.discountedPrice = 0;
    this.savings = 0;
    this.lastCalculatedArea = null;
    this.manuallyAdjustedBuckets = new Set(); // Track buckets that were manually adjusted by user
  }

  /**
   * Initialize coverage rates with defaults and merge custom rates
   */
  initializeCoverageRates(config) {
    // Coverage rates (litres per m²) - merge custom rates with defaults
    // All rates use multiplication: litres = area * rate
    // Using 1000 factor to preserve 3 decimal places for products like sealer (0.125)
    const defaultCoverageRates = {
      'waterproof-sealant': (area, fullCover = true) => Math.round((fullCover ? area * 1.5 : area * 0.2) * 1000) / 1000,
      'protective-top-coat': (area) => Math.round(area * 0.5 * 1000) / 1000,
      sealer: (area) => Math.round(area * 0.125 * 1000) / 1000, // 0.125 L/m² (was area / 8)
      'etch-primer': (area) => Math.round(area * (1 / 6) * 1000) / 1000, // ~0.167 L/m² (was area / 6)
      // rapid-cure-spray is calculated separately based on sealant litres, not area
      // This 0 return is a safeguard - it should never be called through calculateBuckets()
      'rapid-cure-spray': () => 0,
      'geo-textile': () => 0, // Calculated via calculateGeoTextile() based on area multiplier (sealant litres)
      'seam-tape': () => 0, // Not calculated via coverage rates
      'brush-roller': () => 0, // Calculated via calculateVariants() based on area threshold
    };
    this.coverageRates = config.coverageRates
      ? { ...defaultCoverageRates, ...config.coverageRates }
      : defaultCoverageRates;
  }

  /**
   * Bind methods to preserve context
   */
  bindMethods() {
    this.init = this.init.bind(this);
    this.handleLengthInput = this.handleLengthInput.bind(this);
    this.handleRoofTypeToggle = this.handleRoofTypeToggle.bind(this);
    this.handleFullCoverToggle = this.handleFullCoverToggle.bind(this);
    this.handleConcreteRoofToggle = this.handleConcreteRoofToggle.bind(this);
    this.handlePartialAreaToggle = this.handlePartialAreaToggle.bind(this);
    this.handlePartialAreaSlider = this.handlePartialAreaSlider.bind(this);
    this.updateCalculations = this.updateCalculations.bind(this);
  }

  /**
   * Normalize product handle for grouping and coverage lookup.
   * Maps product handles to canonical types for shared logic (e.g. thermal coatings share color state).
   * Unknown handles pass through as-is - kit builder works with any configured products.
   */
  normalizeHandle(handle) {
    if (!handle) return handle;
    const h = String(handle).toLowerCase();
    if (h.includes('waterproof-sealant')) return 'waterproof-sealant';
    if (h.includes('protective-top-coat') || h.includes('thermal-coating')) return 'protective-top-coat';
    if (h.includes('tough-coat') || h.includes('tough_coat')) return 'tough-coat';
    if (h.includes('sealer') && !h.includes('etch')) return 'sealer';
    if (h.includes('etch-primer')) return 'etch-primer';
    if (h.includes('rapid-cure-spray')) return 'rapid-cure-spray';
    if (h.includes('geo-textile') || h.includes('reinforcing-tape') || (h.includes('reinforcing') && h.includes('tape'))) return 'geo-textile';
    if (h.includes('seam-tape')) return 'seam-tape';
    if (h.includes('brush') || h.includes('roller')) return 'brush-roller';
    return handle;
  }

  /** Check if a product type exists in the kit (only run special logic when product is configured) */
  hasProductInKit(normalizedHandle) {
    return this.items.some((i) => this.normalizeHandle(i.data.handle) === normalizedHandle);
  }

  // Helper to update swatch selection state (reduces nesting)
  updateSwatchSelectionState(swatch, swatchLabel, isSelected) {
    requestAnimationFrame(() => {
      if (isSelected) {
        swatch.classList.add('custom-color-swatch--selected');
        if (swatchLabel) {
          swatchLabel.classList.add('custom-color-swatch--selected');
        }
      } else {
        swatch.classList.remove('custom-color-swatch--selected');
        if (swatchLabel) {
          swatchLabel.classList.remove('custom-color-swatch--selected');
        }
      }
    });
  }

  // Helper to find visible input by color value (reduces nesting)
  findVisibleInputByColor(swatchesContainer, colorValue, variantId) {
    if (!colorValue) return null;
    // Find by matching color value
    const allVisibleInputs = swatchesContainer.querySelectorAll('input[type="radio"]');
    for (const vi of allVisibleInputs) {
      if (vi.value === colorValue) {
        return vi;
      }
    }
    // Fallback: try to find by variant ID
    return swatchesContainer.querySelector(`input[data-variant-id="${variantId}"]`);
  }

  // Helper to check input and uncheck others (reduces nesting)
  checkInputAndUncheckOthers(swatchesContainer, inputToCheck) {
    // Uncheck all other inputs first
    swatchesContainer.querySelectorAll('input[type="radio"]').forEach((inp) => {
      inp.checked = false;
    });
    inputToCheck.checked = true;
    inputToCheck.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Get section ID and prefix, caching the result
   */
  getSectionIdentifiers() {
    if (!this._cachedSectionIdOnly) {
      this._cachedSectionIdOnly =
        this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    }
    if (!this._cachedSectionPrefix) {
      this._cachedSectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    }
    return {
      sectionIdOnly: this._cachedSectionIdOnly,
      sectionPrefix: this._cachedSectionPrefix,
    };
  }

  /**
   * Hide the loading state skeleton
   */
  hideLoadingState() {
    const { sectionIdOnly } = this.getSectionIdentifiers();
    try {
      const loadingState = document.getElementById(`calculator-loading-${sectionIdOnly}`);
      if (loadingState) {
        loadingState.style.display = 'none';
        return;
      }
    } catch {
      // Silently ignore
    }
    // Fallback: try querySelector
    const loadingState = document.querySelector(`#calculator-loading-${sectionIdOnly}`);
    if (loadingState) {
      loadingState.style.display = 'none';
    }
  }

  /**
   * Initialize a toggle with default state
   */
  initializeToggle(toggleId, defaultChecked, stateProperty) {
    const { sectionIdOnly, sectionPrefix } = this.getSectionIdentifiers();
    const toggle = document.querySelector(`${sectionPrefix} #${toggleId}-${sectionIdOnly}`);
    if (toggle) {
      toggle.checked = defaultChecked;
      this[stateProperty] = defaultChecked;
    }
  }

  /**
   * Initialize all toggles based on configuration
   */
  initializeToggles() {
    // Initialize roofType toggle if showEtchPrimer is enabled
    if (this.showEtchPrimer && this.showRawMetalToggle) {
      this.initializeToggle('roof-type-toggle', true, 'roofType');
      this.roofType = 'bare-metal';
    }

    // Initialize fullCover toggle if enabled
    if (this.showFullCoverToggle) {
      this.initializeToggle('full-cover-toggle', false, 'fullCover');
    }

    // Initialize concreteRoof toggle if enabled
    if (this.showConcreteRoofToggle) {
      this.initializeToggle('concrete-roof-toggle', false, 'concreteRoof');
    }

    // Initialize partial area toggle if enabled
    if (this.showPartialAreaToggle) {
      this.initializeToggle('partial-area-toggle', false, 'partialAreaEnabled');
    }
  }

  init() {
    // Hide loading state first
    this.hideLoadingState();

    try {
      this.setupEventListeners();
      this.initializeProducts();
      this.initializeToggles();
      this.updateCalculations();
    } catch (error) {
      this.hideLoadingState();
      // Re-throw error for debugging
      throw error;
    }
  }

  setupEventListeners() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly = this.calculatorId || this.sectionId.replace('shopify-section-', '').replace('#', '');

    this.setupLengthInput(sectionPrefix, sectionIdOnly);
    this.setupWidthInput(sectionPrefix, sectionIdOnly);
    this.setupAreaInput(sectionPrefix, sectionIdOnly);
    this.setupToggles(sectionPrefix, sectionIdOnly);
    this.setupToggleTooltips(sectionPrefix);
    this.setupActionButtons(sectionPrefix, sectionIdOnly);

    this.sectionIdOnly = sectionIdOnly;
  }

  setupLengthInput(sectionPrefix, sectionIdOnly) {
    const lengthInput = document.querySelector(`${sectionPrefix} #length-input-${sectionIdOnly}`);
    if (!lengthInput) return;

    let debounceTimer;

    lengthInput.addEventListener('focus', (e) => {
      e.target.select();
    });

    lengthInput.addEventListener('input', (e) => {
      this.handleLengthInput(e);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        const parsed = this.parseDecimal(e.target.value);
        if (parsed !== null && parsed >= this.minLength && parsed <= this.maxLength) {
          this.length = Math.round(parsed * 2) / 2;
          e.target.value = this.length.toFixed(1);
          this.updateCalculations();
        }
      }, 300);
    });

    lengthInput.addEventListener('blur', () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      const parsed = this.parseDecimal(lengthInput.value);
      if (parsed !== null && parsed >= this.minLength && parsed <= this.maxLength) {
        this.length = Math.round(parsed * 2) / 2;
        lengthInput.value = this.length.toFixed(1);
      } else {
        lengthInput.value = this.length.toFixed(1);
      }
      this.updateCalculations();
    });

    lengthInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        lengthInput.blur();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.adjustLength(0.5);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.adjustLength(-0.5);
      }
    });

    const decreaseBtn = document.querySelector(`${sectionPrefix} [data-action="decrease-length"]`);
    const increaseBtn = document.querySelector(`${sectionPrefix} [data-action="increase-length"]`);
    if (decreaseBtn) this.setupHoldButton(decreaseBtn, () => this.adjustLength(-0.5));
    if (increaseBtn) this.setupHoldButton(increaseBtn, () => this.adjustLength(0.5));
  }

  setupWidthInput(sectionPrefix, sectionIdOnly) {
    const widthInput = document.querySelector(`${sectionPrefix} #width-input-${sectionIdOnly}`);
    if (!widthInput) return;

    let debounceTimer;

    widthInput.addEventListener('focus', (e) => {
      e.target.select();
    });

    widthInput.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const parsed = this.parseDecimal(e.target.value);
        if (parsed !== null && parsed >= this.minWidth && parsed <= this.maxWidth) {
          this.width = Math.round(parsed * 10) / 10;
          e.target.value = this.width.toFixed(1);
          this.updateCalculations();
        }
      }, 300);
    });

    widthInput.addEventListener('blur', () => {
      clearTimeout(debounceTimer);
      const parsed = this.parseDecimal(widthInput.value);
      if (parsed !== null && parsed >= this.minWidth && parsed <= this.maxWidth) {
        this.width = Math.round(parsed * 10) / 10;
        widthInput.value = this.width.toFixed(1);
      } else {
        widthInput.value = this.width.toFixed(1);
      }
      this.updateCalculations();
    });

    widthInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        widthInput.blur();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.adjustWidth(0.1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.adjustWidth(-0.1);
      }
    });

    const decreaseBtn = document.querySelector(`${sectionPrefix} [data-action="decrease-width"]`);
    const increaseBtn = document.querySelector(`${sectionPrefix} [data-action="increase-width"]`);
    if (decreaseBtn) this.setupHoldButton(decreaseBtn, () => this.adjustWidth(-0.1));
    if (increaseBtn) this.setupHoldButton(increaseBtn, () => this.adjustWidth(0.1));
  }

  setupAreaInput(sectionPrefix, sectionIdOnly) {
    const areaInput = document.querySelector(`${sectionPrefix} #area-input-${sectionIdOnly}`);
    if (!areaInput) return;

    // On focus, select full value so typing a new number replaces it (not just after decimal).
    areaInput.addEventListener('focus', (e) => {
      e.target.select();
    });

    // Also select all on click so subsequent edits replace the full value too.
    areaInput.addEventListener('click', (e) => {
      e.target.select();
    });

    // Industry standard: user types any value; we never overwrite the field while typing.
    areaInput.addEventListener('input', (e) => {
      const parsed = this.parseDecimal(e.target.value);
      if (parsed !== null) {
        const clamped = Math.max(this.minArea, Math.min(this.maxArea, parsed));
        this.area = Math.round(clamped * 10) / 10;
        this.updateCalculations();
      }
    });

    areaInput.addEventListener('blur', () => {
      const parsed = this.parseDecimal(areaInput.value);
      if (parsed !== null && parsed >= this.minArea && parsed <= this.maxArea) {
        this.area = Math.round(parsed * 10) / 10;
      }
      areaInput.value = this.area.toFixed(1);
      this.updateCalculations();
    });

    areaInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        areaInput.blur();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.adjustArea(0.5);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.adjustArea(-0.5);
      }
    });

    const decreaseBtn = document.querySelector(`${sectionPrefix} [data-action="decrease-area"]`);
    const increaseBtn = document.querySelector(`${sectionPrefix} [data-action="increase-area"]`);
    if (decreaseBtn) this.setupHoldButton(decreaseBtn, () => this.adjustArea(-0.5));
    if (increaseBtn) this.setupHoldButton(increaseBtn, () => this.adjustArea(0.5));
  }

  setupToggles(sectionPrefix, sectionIdOnly) {
    const roofToggle = document.querySelector(`${sectionPrefix} #roof-type-toggle-${sectionIdOnly}`);
    if (roofToggle) {
      roofToggle.addEventListener('change', () => this.handleRoofTypeToggle());
    }

    const fullCoverToggle = document.querySelector(`${sectionPrefix} #full-cover-toggle-${sectionIdOnly}`);
    if (fullCoverToggle) {
      fullCoverToggle.addEventListener('change', () => this.handleFullCoverToggle());
    }

    const concreteRoofToggle = document.querySelector(`${sectionPrefix} #concrete-roof-toggle-${sectionIdOnly}`);
    if (concreteRoofToggle) {
      concreteRoofToggle.addEventListener('change', () => this.handleConcreteRoofToggle());
    }

    const partialAreaToggle = document.querySelector(`${sectionPrefix} #partial-area-toggle-${sectionIdOnly}`);
    if (partialAreaToggle) {
      partialAreaToggle.addEventListener('change', () => this.handlePartialAreaToggle());
    }

    const partialAreaSlider = document.querySelector(`${sectionPrefix} #partial-area-slider-${sectionIdOnly}`);
    if (partialAreaSlider) {
      partialAreaSlider.addEventListener('input', () => this.handlePartialAreaSlider());
    }

    // Sync partial area slider wrap visibility with toggle state on load
    if (this.showPartialAreaToggle) {
      const partialAreaSliderWrap = document.querySelector(`${sectionPrefix} #partial-area-slider-wrap-${sectionIdOnly}`);
      const partialAreaToggleEl = document.querySelector(`${sectionPrefix} #partial-area-toggle-${sectionIdOnly}`);
      if (partialAreaSliderWrap && partialAreaToggleEl) {
        partialAreaSliderWrap.style.display = partialAreaToggleEl.checked ? '' : 'none';
      }
    }
  }

  setupToggleTooltips(sectionPrefix) {
    const toggleTooltipTriggers = document.querySelectorAll(`${sectionPrefix} .toggle-tooltip-trigger`);
    toggleTooltipTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const tooltipContainer = trigger.closest('.toggle-tooltip-container');
        if (!tooltipContainer) return;

        const isActive = tooltipContainer.classList.contains('active');
        this.closeOtherTooltips(sectionPrefix, tooltipContainer);

        if (isActive) {
          tooltipContainer.classList.remove('active');
        } else {
          tooltipContainer.classList.add('active');
          this.adjustTooltipPositionOnMobile(tooltipContainer);
        }
      });
    });
  }

  closeOtherTooltips(sectionPrefix, currentContainer) {
    const allTooltipContainers = document.querySelectorAll(
      `${sectionPrefix} .toggle-tooltip-container, ${sectionPrefix} .product-why-tooltip-container`
    );
    allTooltipContainers.forEach((container) => {
      if (container !== currentContainer) {
        container.classList.remove('active');
      }
    });
  }

  adjustTooltipPositionOnMobile(tooltipContainer) {
    if (window.innerWidth > 749) return;

    const tooltip = tooltipContainer.querySelector('.toggle-tooltip');
    if (!tooltip) return;

    tooltipContainer.classList.remove('tooltip-left');

    requestAnimationFrame(() => {
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const padding = 16;
      const overflowsLeft = tooltipRect.left < padding;

      if (overflowsLeft) {
        tooltipContainer.classList.add('tooltip-left');
        requestAnimationFrame(() => {
          const newTooltipRect = tooltip.getBoundingClientRect();
          const overflowsRight = newTooltipRect.right > viewportWidth - padding;
          if (overflowsRight) {
            tooltipContainer.classList.remove('tooltip-left');
          }
        });
      } else {
        tooltipContainer.classList.remove('tooltip-left');
      }
    });
  }

  setupActionButtons(sectionPrefix, sectionIdOnly) {
    const resetBtn = document.querySelector(`${sectionPrefix} #reset-calculator-${sectionIdOnly}`);
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.resetToDefaults());
    }

    const closePopupBtn = document.querySelector(`${sectionPrefix} .close-popup`);
    if (closePopupBtn) {
      closePopupBtn.addEventListener('click', () => this.closePopup());
    }

    const ctaBtn = document.querySelector(`${sectionPrefix} #cta-kit-builder-${sectionIdOnly}`);
    if (ctaBtn) {
      ctaBtn.addEventListener('click', () => this.goToCheckout());
    }
  }

  setupHoldButton(button, callback) {
    let holdTimeout;
    let holdInterval;

    const startHold = () => {
      callback();
      holdTimeout = setTimeout(() => {
        holdInterval = setInterval(callback, 75);
      }, 300);
    };

    const clearHold = () => {
      clearTimeout(holdTimeout);
      clearInterval(holdInterval);
    };

    button.addEventListener('mousedown', startHold);
    button.addEventListener('mouseup', clearHold);
    button.addEventListener('mouseleave', clearHold);
    button.addEventListener('touchstart', startHold, { passive: true });
    button.addEventListener('touchend', clearHold);
  }

  handleLengthInput(e) {
    const value = e.target.value.replaceAll(/[^\d,.\s]/g, '').replaceAll(/\s+/g, '');
    e.target.value = value;
  }

  adjustLength(delta) {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const lengthInput = document.querySelector(`#length-input-${sectionIdOnly}`);
    if (!lengthInput) return;

    // Use this.length as source of truth instead of reading from input field
    // This ensures calculations are always based on the current value, not stale input values
    let current = this.length || this.minLength;
    current = Math.max(this.minLength, Math.min(this.maxLength, current + delta));
    current = Math.round(current * 2) / 2;

    this.length = current;
    lengthInput.value = current.toFixed(1);
    this.updateCalculations();
  }

  adjustWidth(delta) {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const widthInput = document.querySelector(`#width-input-${sectionIdOnly}`);
    if (!widthInput) return;

    let current = this.width || this.minWidth;
    current = Math.max(this.minWidth, Math.min(this.maxWidth, current + delta));
    current = Math.round(current * 10) / 10;

    this.width = current;
    widthInput.value = current.toFixed(1);
    this.updateCalculations();
  }

  adjustArea(delta) {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const areaInput = document.querySelector(`#area-input-${sectionIdOnly}`);
    if (!areaInput) return;

    let current = this.area || this.minArea;
    current = Math.max(this.minArea, Math.min(this.maxArea, current + delta));
    current = Math.round(current * 2) / 2; // Round to 0.5 increments

    this.area = current;
    areaInput.value = current.toFixed(1);
    this.updateCalculations();
  }

  parseDecimal(str) {
    if (!str) return null;
    let s = str.trim().replaceAll(/\s/g, '');
    if (s.includes(',') && s.includes('.')) {
      const lastComma = s.lastIndexOf(',');
      const lastDot = s.lastIndexOf('.');
      if (lastComma > lastDot) {
        s = s.replaceAll('.', '').replace(',', '.');
      } else {
        s = s.replaceAll(',', '');
      }
    } else {
      s = s.replace(',', '.');
    }
    s = s.replaceAll(/[^0-9.-]/g, '');
    if (s.startsWith('.')) s = '0' + s;
    const num = Number(s);
    return Number.isFinite(num) ? num : null;
  }

  handleRoofTypeToggle() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const toggle = document.querySelector(`#roof-type-toggle-${sectionIdOnly}`);
    if (toggle) {
      this.roofType = toggle.checked ? 'bare-metal' : 'painted-roof';
    }

    // Only update primer products (sealer/etch-primer), not everything
    this.updatePrimerProductsOnly();
  }

  handleFullCoverToggle() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const toggle = document.querySelector(`#full-cover-toggle-${sectionIdOnly}`);
    if (toggle) {
      this.fullCover = toggle.checked;
    }

    // Recalculate all products when full cover changes
    this.updateCalculations();
  }

  handleConcreteRoofToggle() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const toggle = document.querySelector(`#concrete-roof-toggle-${sectionIdOnly}`);
    if (toggle) {
      this.concreteRoof = toggle.checked;
    }

    // Recalculate all products when concrete roof changes
    this.updateCalculations();
  }

  handlePartialAreaToggle() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const toggle = document.querySelector(`${sectionPrefix} #partial-area-toggle-${sectionIdOnly}`);
    if (toggle) {
      this.partialAreaEnabled = toggle.checked;
    }

    const sliderWrap = document.querySelector(`${sectionPrefix} #partial-area-slider-wrap-${sectionIdOnly}`);
    if (sliderWrap) {
      sliderWrap.style.display = this.partialAreaEnabled ? '' : 'none';
    }

    // Sync partial area percent from slider when showing (e.g. after theme default changed)
    if (this.partialAreaEnabled) {
      const slider = document.querySelector(`${sectionPrefix} #partial-area-slider-${sectionIdOnly}`);
      if (slider) {
        const val = Number(slider.value);
        if (!Number.isNaN(val)) this.partialAreaPercent = Math.min(100, Math.max(0, val));
      }
      this.updatePartialAreaSliderDisplay();
    }

    this.updateCalculations();
  }

  handlePartialAreaSlider() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const slider = document.querySelector(`${sectionPrefix} #partial-area-slider-${sectionIdOnly}`);
    if (slider) {
      const val = Number(slider.value) || this.partialAreaSliderDefault;
      this.partialAreaPercent = Math.min(100, Math.max(0, val));
      slider.setAttribute('aria-valuenow', String(this.partialAreaPercent));
    }

    this.updatePartialAreaSliderDisplay();
    this.updateCalculations();
  }

  /**
   * Update the partial area slider value text to show percentage and calculated area (e.g. "20% (6 m²)")
   */
  updatePartialAreaSliderDisplay() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const slider = document.querySelector(`${sectionPrefix} #partial-area-slider-${sectionIdOnly}`);
    const valueEl = document.querySelector(`${sectionPrefix} #partial-area-slider-value-${sectionIdOnly}`);
    if (!valueEl) return;

    if (slider) {
      const min = Number(slider.min || 0);
      const max = Number(slider.max || 100);
      const value = Number(slider.value || this.partialAreaPercent || 0);
      const percent = max > min ? ((value - min) / (max - min)) * 100 : 0;
      const clamped = Math.max(0, Math.min(100, percent));
      slider.style.background = `linear-gradient(to right, #22c55e 0%, #22c55e ${clamped}%, rgba(255, 255, 255, 0.2) ${clamped}%, rgba(255, 255, 255, 0.2) 100%)`;
    }

    const area = this.getArea();
    const effectiveArea = (area * this.partialAreaPercent) / 100;
    const areaStr = effectiveArea % 1 === 0 ? String(Math.round(effectiveArea)) : effectiveArea.toFixed(1);
    valueEl.textContent = `${this.partialAreaPercent}% (${areaStr} m²)`;
  }

  resetToDefaults() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');

    // Reset length to default
    this.length = this.defaultLength;
    const lengthInput = document.querySelector(`#length-input-${sectionIdOnly}`);
    if (lengthInput) {
      lengthInput.value = this.defaultLength.toFixed(1);
    }

    // Reset width to default
    this.width = this.defaultWidth;
    const widthInput = document.querySelector(`#width-input-${sectionIdOnly}`);
    if (widthInput) {
      widthInput.value = this.defaultWidth.toFixed(1);
    }

    // Reset area to default
    this.area = this.defaultArea;
    const areaInput = document.querySelector(`#area-input-${sectionIdOnly}`);
    if (areaInput) {
      areaInput.value = this.defaultArea.toFixed(1);
    }

    // Reset roof type to default (painted-roof)
    this.roofType = 'painted-roof';
    const roofToggle = document.querySelector(`#roof-type-toggle-${sectionIdOnly}`);
    if (roofToggle) {
      roofToggle.checked = false;
    }

    // Reset full cover to default (false)
    this.fullCover = false;
    const fullCoverToggle = document.querySelector(`#full-cover-toggle-${sectionIdOnly}`);
    if (fullCoverToggle) {
      fullCoverToggle.checked = false;
    }

    // Reset concrete roof to default (false)
    this.concreteRoof = false;
    const concreteRoofToggle = document.querySelector(`#concrete-roof-toggle-${sectionIdOnly}`);
    if (concreteRoofToggle) {
      concreteRoofToggle.checked = false;
    }

    // Reset partial area toggle and slider to default percentage
    this.partialAreaEnabled = false;
    this.partialAreaPercent = this.partialAreaSliderDefault;
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const partialAreaToggle = document.querySelector(`${sectionPrefix} #partial-area-toggle-${sectionIdOnly}`);
    if (partialAreaToggle) {
      partialAreaToggle.checked = false;
    }
    const partialAreaSliderWrap = document.querySelector(`${sectionPrefix} #partial-area-slider-wrap-${sectionIdOnly}`);
    if (partialAreaSliderWrap) {
      partialAreaSliderWrap.style.display = 'none';
    }
    const partialAreaSlider = document.querySelector(`${sectionPrefix} #partial-area-slider-${sectionIdOnly}`);
    if (partialAreaSlider) {
      partialAreaSlider.value = String(this.partialAreaSliderDefault);
      partialAreaSlider.setAttribute('aria-valuenow', String(this.partialAreaSliderDefault));
    }
    const partialAreaSliderValue = document.querySelector(`${sectionPrefix} #partial-area-slider-value-${sectionIdOnly}`);
    if (partialAreaSliderValue) {
      const area = this.getArea();
      const effectiveArea = (area * this.partialAreaSliderDefault) / 100;
      const areaStr = effectiveArea % 1 === 0 ? String(Math.round(effectiveArea)) : effectiveArea.toFixed(1);
      partialAreaSliderValue.textContent = `${this.partialAreaSliderDefault}% (${areaStr} m²)`;
    }

    // Reset colors to defaults (white for thermal coating)
    this.selectedColors = this.products.reduce((acc, p) => {
      if (p.handle === 'seam-tape') return acc;
      const normalizedHandle = this.normalizeHandle(p.handle);

      // Set default color to white for thermal coating (protective-top-coat)
      if (normalizedHandle === 'protective-top-coat' && p.variants) {
        // Find white variant
        const whiteVariant = p.variants.find((v) => {
          const color = (v.color || '').toString().trim().toLowerCase();
          return color === 'white';
        });
        if (whiteVariant?.color) {
          acc[normalizedHandle] = whiteVariant.color;
        } else if (p.colors?.length) {
          acc[normalizedHandle] = p.colors[0];
        }
      } else if (p.colors?.length) {
        acc[normalizedHandle] = p.colors[0];
      }
      return acc;
    }, {});

    // Clear any manually adjusted bucket counts by clearing breakdowns
    // This will force a fresh calculation
    this.breakdowns = [];
    this.manuallyAdjustedBuckets.clear();
    this.lastCalculatedArea = null;

    // Recalculate everything
    this.updateCalculations();
  }

  initializeProducts() {
    // Initialize items and selected colors
    // Exclude seam-tape and liquid-rubber-diy-2-piece-brush-kit (brush kit is in bonus, not calculator)
    this.items = this.products
      .filter(
        (p) =>
          p.handle !== 'seam-tape' &&
          p.handle !== 'liquid-rubber-diy-2-piece-brush-kit' &&
          p.handle !== this.partialAreaProductHandle
      )
      .map((p) => ({ data: p, selected: true }));

    this.selectedColors = this.products.reduce((acc, p) => {
      if (p.handle === 'seam-tape' || p.handle === 'liquid-rubber-diy-2-piece-brush-kit') return acc;
      const normalizedHandle = this.normalizeHandle(p.handle);

      // Set default color to white for thermal coating (protective-top-coat)
      if (normalizedHandle === 'protective-top-coat' && p.variants) {
        // Find white variant
        const whiteVariant = p.variants.find((v) => {
          const color = (v.color || '').toString().trim().toLowerCase();
          return color === 'white';
        });
        if (whiteVariant?.color) {
          acc[normalizedHandle] = whiteVariant.color;
        } else if (p.colors?.length) {
          acc[normalizedHandle] = p.colors[0];
        }
      } else if (p.colors?.length) {
        acc[normalizedHandle] = p.colors[0];
      }
      return acc;
    }, {});
  }

  getArea() {
    if (this.inputMode === 'area') {
      return Math.round(this.area * 100) / 100;
    } else if (this.inputMode === 'length_width') {
      return Math.round(this.length * this.width * 100) / 100;
    } else {
      // length mode: use length * default width
      return Math.round(this.length * this.defaultWidth * 100) / 100;
    }
  }

  /**
   * Convert a URL slug/handle into a human-readable title.
   * e.g. "waterproof-sealant" -> "Waterproof Sealant"
   */
  normalizeSlugToTitle(slug) {
    if (!slug || typeof slug !== 'string') return '';
    const cleaned = slug.replace(/[-_]+/g, ' ').trim();
    if (!cleaned) return '';
    return cleaned
      .split(' ')
      .filter((part) => part)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  /**
   * Build a human-readable cart note based on the current calculator inputs
   * and the page where the kit was configured.
   *
   * Examples:
   * - /products/waterproof-sealant  -> "Waterproof Sealant 25 m² (added from /products/waterproof-sealant)"
   * - /collections/caravans        -> "Caravan, 6 m (added from /collections/caravans)"
   */
  buildCartNoteFromCalculator() {
    const path = (globalThis.location && globalThis.location.pathname) || '';
    const area = this.getArea();

    // Always display the calculated area in the note
    const valueText = `${this.formatDecimal(area)} m²`;

    // Decide label based on URL and page title
    const pathParts = path.split('/').filter(Boolean);
    const handleFromPath = pathParts.length ? pathParts[pathParts.length - 1] : '';
    const normalizedTitleFromPath = this.normalizeSlugToTitle(handleFromPath);

    let label = (this.pageTitle && this.pageTitle.trim()) || '';

    if (path.startsWith('/collections/')) {
      // Collection pages: prefer actual collection title if available, otherwise
      // create a readable name from the handle, finally fall back to "Collection"
      if (!label) {
        label = normalizedTitleFromPath || 'Collection';
      }
    } else if (path.startsWith('/products/')) {
      // Product pages: prefer product title, otherwise titleized handle, then "Product"
      if (!label) {
        label = normalizedTitleFromPath || 'Product';
      }
    } else if (!label) {
      // Fallback for other pages
      label = 'Kit';
    }

    // For collection-style label we add a comma: "Caravan, 6 m"
    const needsComma = path.startsWith('/collections/');
    const baseLabel = needsComma ? `${label},` : label;

    return `${baseLabel} ${valueText}`;
  }

  /**
   * Update the cart note on Shopify with the current calculator details.
   * Uses the same /cart/update.js mechanism as the built-in cart note.
   */
  async updateCartNoteFromCalculator() {
    try {
      const note = this.buildCartNoteFromCalculator();
      if (!note) return;

      const updateUrl =
        (typeof routes !== 'undefined' && routes.cart_update_url) ||
        (globalThis.routes && globalThis.routes.cart_update_url) ||
        '/cart/update.js';

      let config;
      if (typeof fetchConfig !== 'undefined') {
        config = fetchConfig('javascript');
      } else {
        config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        };
      }

      // Ensure JSON body for cart/update endpoint
      config.headers = config.headers || {};
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify({ note });

      await fetch(updateUrl, config);
    } catch {
      // Fail silently
    }
  }

  /**
   * Format a number to display with up to 3 decimal places, removing trailing zeros
   * @param {number} num - The number to format
   * @returns {string} - Formatted number string
   */
  formatDecimal(num) {
    if (num == null || Number.isNaN(num)) return '0';
    // Round to 3 decimal places to handle floating point precision issues
    const rounded = Math.round(num * 1000) / 1000;
    // Convert to string with up to 3 decimal places, then remove trailing zeros
    return rounded.toFixed(3).replace(/\.?0+$/, '');
  }

  isVisible(handle) {
    // Partial area product (e.g. product 10) only visible when toggle is on
    if (this.partialAreaProductHandle && handle === this.partialAreaProductHandle) {
      return this.showPartialAreaToggle && this.partialAreaEnabled;
    }
    // If showEtchPrimer is enabled, always show etch-primer (as if toggle is on)
    if (handle === 'etch-primer' && this.showEtchPrimer) return true;
    // If raw metal toggle is not shown and showEtchPrimer is not enabled, never show etch-primer
    if (handle === 'etch-primer' && !this.showRawMetalToggle && !this.showEtchPrimer) return false;
    if (handle === 'sealer') return this.roofType !== 'bare-metal';
    if (handle === 'etch-primer') return this.roofType === 'bare-metal';
    if (handle === 'seam-tape') return false;
    if (handle === 'brush-roller') return false;
    if (handle === 'liquid-rubber-diy-2-piece-brush-kit') return false;
    return true;
  }

  /**
   * Whether to show the "Recommended color" text for this product.
   * Only the product selected in "Show recommended color for this product" shows it.
   */
  showRecommendedColorForHandle(handle) {
    if (!this.showRecommendedColorProductHandle) return false;
    return this.normalizeHandle(handle) === this.normalizeHandle(this.showRecommendedColorProductHandle);
  }

  calculateLitresNeeded(product, handle, area, fullCover) {
    // Check if concrete roof is enabled and product has rough surface coverage
    if (this.concreteRoof && product.coverageRough && product.coverageRough > 0) {
      // Use rough surface coverage
      const areaPercentage = product.areaPercentage || 1;
      const isWaterproofSealant = handle === 'waterproof-sealant';
      const effectiveAreaPercentage = isWaterproofSealant && fullCover ? 1 : areaPercentage;
      const effectiveArea = Number(area) * Number(effectiveAreaPercentage);
      const litres = effectiveArea * Number(product.coverageRough);
      return Math.round(litres * 1000) / 1000;
    }

    // Use normal coverage rate function
    // Try normalized handle first, then original handle
    if (this.coverageRates[handle]) {
      return this.coverageRates[handle](area, fullCover);
    }
    if (this.coverageRates[product.handle]) {
      return this.coverageRates[product.handle](area, fullCover);
    }
    return 0;
  }

  calculateBuckets(product, area, fullCover = true) {
    if (!product || area <= 0) return [];

    const handle = this.normalizeHandle(product.handle);

    // rapid-cure-spray is calculated separately based on sealant litres, not area
    // It should never go through this method - early return as safeguard
    if (handle === 'rapid-cure-spray') {
      return [];
    }

    if (handle === 'geo-textile') {
      return this.calculateGeoTextile(product, area);
    }

    const variants = this.extractBucketVariants(product);
    if (!variants.length) {
      return [];
    }

    const litresNeeded = this.calculateLitresNeeded(product, handle, area, fullCover);
    if (litresNeeded <= 0) return [];

    const result = this.findOptimalCombo(litresNeeded, variants);
    return result;
  }

  extractBucketVariants(product) {
    const bySize = new Map();
    product.variants.forEach((v) => {
      // Match both "L" (liters) and "kg" (kilograms) - treat kg the same as L for calculation
      const m = /(\d+(?:\.\d+)?)\s*(?:L|kg)/i.exec(v.label || '');
      if (!m) {
        return;
      }
      const size = +m[1];
      const price = +v.price;
      if (!bySize.has(size) || price < bySize.get(size).price) {
        bySize.set(size, {
          size,
          price,
          variantId: v.variantId,
          imageUrl: v.imageUrl || '/placeholder.png',
          label: v.label || '',
          color: v.color || '',
        });
      }
    });

    const variants = Array.from(bySize.values()).sort((a, b) => b.size - a.size);
    return variants;
  }

  findOptimalCombo(litresNeeded, variants) {
    // Use factor 1000 to support 3 decimal places (e.g., 0.125)
    const factor = 1000;
    const needVol = Math.ceil(litresNeeded * factor);
    const biggest = Math.round(variants[0].size * factor);
    const cap = needVol + biggest;

    const dp = new Array(cap + 1).fill(null);
    dp[0] = { cost: 0, counts: new Array(variants.length).fill(0) };

    for (let vol = 0; vol <= cap; vol++) {
      const entry = dp[vol];
      if (!entry) continue;

      variants.forEach((v, i) => {
        const nextVol = vol + Math.round(v.size * factor);
        if (nextVol > cap) return;

        const currentCount = entry.counts[i];
        const isLargest = i === 0;
        if (!isLargest && currentCount >= 3) return;

        const nextCost = entry.cost + v.price;
        const nextCounts = entry.counts.slice();
        nextCounts[i]++;

        const prev = dp[nextVol];
        if (!prev || nextCost < prev.cost) {
          dp[nextVol] = { cost: nextCost, counts: nextCounts };
        }
      });
    }

    let best = null;
    for (let vol = needVol; vol <= cap; vol++) {
      const entry = dp[vol];
      if (!entry) continue;
      if (!best || entry.cost < best.entry.cost) {
        best = { vol, entry };
      }
    }

    if (!best) return [];

    return variants.flatMap((v, i) => {
      const c = best.entry.counts[i];
      return c === 0
        ? []
        : [
            {
              size: v.size,
              count: c,
              price: v.price,
              variantId: v.variantId,
              imageUrl: v.imageUrl,
              label: v.label,
              color: v.color,
            },
          ];
    });
  }

  getLengthFromLabel(label) {
    if (!label) return undefined;
    const s = String(label).trim();
    // LRIDY-style: "20 x 1 m" or "x 20 m"
    let m = /x\s*(\d+)\s*m\b/i.exec(s);
    if (m) return Number.parseInt(m[1], 10);
    // NetZero-style: "20m", "50 m", "100m roll"
    m = /(\d+)\s*m\b/i.exec(s);
    return m ? Number.parseInt(m[1], 10) : undefined;
  }

  calculateGeoTextileRemainder(remainder) {
    if (remainder <= 20) {
      return { count100: 0, count50: 0, count20: 1 };
    }
    if (remainder <= 50) {
      return { count100: 0, count50: 1, count20: 0 };
    }
    if (remainder <= 70) {
      return { count100: 0, count50: 1, count20: 1 };
    }
    return { count100: 1, count50: 0, count20: 0 };
  }

  optimizeGeoTextileCounts(counts) {
    let { count100, count50, count20 } = counts;

    if (count20 >= 2) {
      count50 += Math.floor(count20 / 2);
      count20 = count20 % 2;
    }
    if (count50 >= 2) {
      count100 += Math.floor(count50 / 2);
      count50 = count50 % 2;
    }

    return { count100, count50, count20 };
  }

  buildGeoTextileResult(counts, variants) {
    const result = [];
    const { count100, count50, count20 } = counts;

    if (count100 > 0 && variants.v100) {
      result.push({ ...variants.v100, count: count100, size: variants.v100.size });
    }
    if (count50 > 0 && variants.v50) {
      result.push({ ...variants.v50, count: count50, size: variants.v50.size });
    }
    if (count20 > 0 && variants.v20) {
      result.push({ ...variants.v20, count: count20, size: variants.v20.size });
    }

    return result;
  }

  calculateGeoTextile(product, area) {
    // Use area multiplier if available, otherwise default to 1
    // Rule: 1m of geo textile / reinforcing tape per 1L of sealant or coating (multiplied by area multiplier)
    const multiplier = product.multiplier || 1;

    // Prefer waterproof sealant litres; fallback to coating (protective-top-coat) for NetZero-style kits
    let sealantItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'waterproof-sealant');
    if (!sealantItem) {
      sealantItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'protective-top-coat');
    }

    if (!sealantItem) {
      // No sealant or coating in kit – return empty result
      return this.buildGeoTextileResult(
        { count100: 0, count50: 0, count20: 0 },
        {
          v100: null,
          v50: null,
          v20: null,
        }
      );
    }

    // Calculate base litres (sealant or coating) needed for the area
    const useFull = this.fullCover;
    const baseHandle = this.normalizeHandle(sealantItem.data.handle);
    let sealantLitres = 0;

    if (this.concreteRoof && sealantItem.data.coverageRough && sealantItem.data.coverageRough > 0) {
      const areaPercentage = sealantItem.data.areaPercentage || 1;
      const effectiveAreaPercentage = useFull ? 1 : areaPercentage;
      const effectiveArea = Number(area) * Number(effectiveAreaPercentage);
      sealantLitres = effectiveArea * Number(sealantItem.data.coverageRough);
      sealantLitres = Math.round(sealantLitres * 1000) / 1000;
    } else if (this.coverageRates[baseHandle]) {
      sealantLitres = this.coverageRates[baseHandle](area, useFull);
    }

    // Calculate geo textile: 1m per L of sealant, multiplied by area multiplier
    const requiredMeters = Math.ceil(sealantLitres * multiplier);

    const variants = product.variants.filter((v) => v.size !== undefined);

    const variantMap = {
      v100: variants.find((v) => this.getLengthFromLabel(v.label) === 100),
      v50: variants.find((v) => this.getLengthFromLabel(v.label) === 50),
      v20: variants.find((v) => this.getLengthFromLabel(v.label) === 20),
    };

    let counts = { count100: 0, count50: 0, count20: 0 };

    if (requiredMeters > 0) {
      counts.count100 = Math.floor(requiredMeters / 100);
      const remainder = requiredMeters % 100;

      if (remainder > 0) {
        const remainderCounts = this.calculateGeoTextileRemainder(remainder);
        counts.count100 += remainderCounts.count100;
        counts.count50 += remainderCounts.count50;
        counts.count20 += remainderCounts.count20;
      }

      counts = this.optimizeGeoTextileCounts(counts);
    }

    return this.buildGeoTextileResult(counts, variantMap);
  }

  calculateVariants(product, area) {
    const handle = this.normalizeHandle(product.handle);

    // Check if product has a multiplier (length-based product)
    if (product.multiplier && product.multiplier > 0) {
      return this.calculateGeoTextile(product, area);
    }

    if (handle === 'geo-textile') {
      return this.calculateGeoTextile(product, area);
    }

    if (handle === 'brush-roller') {
      const variants = product.variants || [];
      const isBrushOnly = area < 5;
      const chosen =
        variants.find((v) => {
          const label = (v.label || '').toLowerCase();
          const hasBrush = label.includes('brush');
          const hasRoller = label.includes('roller') || label.includes('+');
          return isBrushOnly ? hasBrush && !hasRoller : hasRoller;
        }) || variants[0];

      if (!chosen) return [];

      return [
        {
          ...chosen,
          count: 1,
          imageUrl: isBrushOnly
            ? '/images/products/liquid-rubber-diy-brush-set-caravan-roof.webp'
            : '/images/products/liquid-rubber-diy-brush-roller-set-caravan-roof.webp',
        },
      ];
    }

    if (handle === 'rapid-cure-spray') {
      // This is calculated based on sealant litres, handled separately
      return [];
    }

    return [];
  }

  updateCalculations() {
    const area = this.getArea();
    this.updateLengthDisplay();
    this.updateAreaDisplay();

    // Track previous area to determine if we should preserve manual counts
    this.lastCalculatedArea = area;

    // Preserve manually adjusted bucket counts before recalculating
    // Only preserve counts for buckets that were manually adjusted by the user
    // Key format: handle:variantId -> count (using variantId as primary key for better matching)
    const manualCounts = new Map();
    if (this.breakdowns && this.breakdowns.length > 0) {
      this.breakdowns.forEach((bd) => {
        bd.buckets.forEach((bucket) => {
          // Only preserve counts for buckets that were manually adjusted
          if (bucket.variantId) {
            const key = `${bd.handle}:${bucket.variantId}`;
            if (this.manuallyAdjustedBuckets.has(key)) {
              // Store the manually adjusted count
              manualCounts.set(key, bucket.count);
            }
          }
        });
      });
    }

    // Step 1: Sort items to ensure waterproof-sealant is calculated FIRST
    // This is critical because rapid-cure-spray depends on sealant litres
    const sortedItems = [...this.items].sort((a, b) => {
      const aHandle = this.normalizeHandle(a.data.handle);
      const bHandle = this.normalizeHandle(b.data.handle);

      // waterproof-sealant should come first
      if (aHandle === 'waterproof-sealant') return -1;
      if (bHandle === 'waterproof-sealant') return 1;

      // rapid-cure-spray should come last (will be filtered out anyway)
      if (aHandle === 'rapid-cure-spray') return 1;
      if (bHandle === 'rapid-cure-spray') return -1;

      return 0;
    });

    // Step 2: Calculate breakdowns for all items except rapid-cure-spray
    // This ensures waterproof-sealant is fully calculated first
    const prelim = sortedItems
      .map((item) => {
        const breakdown = this.calculateItemBreakdown(item, area);
        return breakdown;
      })
      .filter((b) => b.handle !== 'rapid-cure-spray');

    // Step 4: Calculate rapid-cure-spray AFTER sealant (only when both are in kit)
    let list = this.addRapidCureSprayIfNeeded(prelim);

    // Step 4b: Add partial area product (e.g. product 10) when toggle is on – quantity from area × slider %
    if (
      this.showPartialAreaToggle &&
      this.partialAreaEnabled &&
      this.partialAreaProductHandle
    ) {
      const partialProduct = this.products.find((p) => p.handle === this.partialAreaProductHandle);
      if (partialProduct) {
        const effectiveArea = (area * this.partialAreaPercent) / 100;
        const partialItem = { data: partialProduct, selected: true };
        const partialBreakdown = this.calculateItemBreakdown(partialItem, effectiveArea);
        list.push(partialBreakdown);
      }
    }

    // Step 5: Restore manually adjusted bucket counts
    // Match by variantId to ensure we're restoring the correct bucket
    // Only restore if the bucket still exists in the new calculation
    if (manualCounts.size > 0) {
      // Track which buckets still exist in the new calculation
      const existingBuckets = new Set();

      list.forEach((bd) => {
        bd.buckets.forEach((bucket) => {
          if (bucket.variantId) {
            const key = `${bd.handle}:${bucket.variantId}`;
            existingBuckets.add(key);
            if (manualCounts.has(key)) {
              // Restore the manually adjusted count
              // This preserves user's manual adjustments even when area changes
              const preservedCount = manualCounts.get(key);
              bucket.count = preservedCount;
            }
          }
        });
      });

      // Clean up manually adjusted buckets that no longer exist in the calculation
      this.manuallyAdjustedBuckets.forEach((key) => {
        if (!existingBuckets.has(key)) {
          this.manuallyAdjustedBuckets.delete(key);
        }
      });
    }

    // Organize breakdowns (brush-roller last)
    this.breakdowns = this.organizeBreakdowns(list);

    // Update UI - try to update only variants first (preserves color swatches)
    // If products don't exist yet, fall back to full render
    if (!this.updateVariantsOnly()) {
      this.renderProductBreakdown();
    }
    this.updatePricing();
    // Update partial area slider display (percentage + calculated area) when area or slider changes
    if (this.showPartialAreaToggle && this.partialAreaEnabled) {
      this.updatePartialAreaSliderDisplay();
    }
    // Update bonus kit (async - fire and forget, will update UI when variant info is fetched)
    this.updateBonusKit(area).catch(() => {});
  }

  /**
   * Helper method to update a single primer product (sealer or etch-primer)
   * @param {Object} item - The product item to update
   * @param {string} handle - The normalized handle ('sealer' or 'etch-primer')
   * @param {number} area - The calculated area
   * @param {HTMLElement} container - The product breakdown container element
   */
  updateSinglePrimerProduct(item, handle, area, container) {
    // Ensure breakdown exists - if not, calculate it without full render
    let breakdown = this.breakdowns.find((bd) => this.normalizeHandle(bd.handle) === handle);
    if (!breakdown) {
      breakdown = this.calculateItemBreakdown(item, area);
      this.breakdowns.push(breakdown);
    }

    // Recalculate buckets
    const buckets = this.calculateBuckets(item.data, area, true);
    const matchedBuckets = item.data.hasColorVariants ? this.matchVariantsWithColors(buckets, item, handle) : buckets;
    breakdown.buckets = matchedBuckets;

    // Update UI
    const productCard = container.querySelector(`.product-card[data-handle="${handle}"]`);
    if (!productCard) {
      return; // Product card doesn't exist - will be created on next full render
    }

    const isVisible = this.isVisible(handle);
    productCard.style.display = isVisible ? '' : 'none';

    if (!isVisible) {
      return; // Product is hidden, no need to update variants
    }

    // Update variants column only (preserves color swatches in title column)
    const variantsColumn = productCard.querySelector('.product-card-variants-column');
    if (!variantsColumn) {
      return;
    }

    const variantsDiv = variantsColumn.querySelector('.product-card-variants');
    if (!variantsDiv) {
      return;
    }

    // Use a temporary marker to prevent mutation observers from interfering
    variantsDiv.dataset.updating = 'true';
    variantsDiv.innerHTML = '';
    this.renderProductVariants(breakdown, variantsDiv);
    delete variantsDiv.dataset.updating;
  }

  /**
   * Update only the primer products (sealer/etch-primer) when roof type changes
   * This shows/hides the appropriate product and updates its variants without affecting other products
   */
  updatePrimerProductsOnly() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const container = document.querySelector(`#product-breakdown-${sectionIdOnly}`);
    if (!container) {
      this.updateCalculations();
      return;
    }

    const area = this.getArea();

    // Update sealer product
    const sealerItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'sealer');
    if (sealerItem) {
      this.updateSinglePrimerProduct(sealerItem, 'sealer', area, container);
    }

    // Update etch-primer product
    const etchPrimerItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'etch-primer');
    if (etchPrimerItem) {
      this.updateSinglePrimerProduct(etchPrimerItem, 'etch-primer', area, container);
    }

    // Update pricing
    this.updatePricing();
  }

  /**
   * Update only the variants column for all products without touching color swatches
   * Returns true if update was successful, false if products don't exist yet (needs full render)
   */
  updateVariantsOnly() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const container = document.querySelector(`#product-breakdown-${sectionIdOnly}`);
    if (!container) return false;

    let allUpdated = true;

    this.breakdowns.forEach((bd) => {
      if (!this.isVisible(bd.handle)) return;

      const productCard = container.querySelector(`.product-card[data-handle="${bd.handle}"]`);
      if (!productCard) {
        allUpdated = false;
        return;
      }

      // Find the variants column
      const variantsColumn = productCard.querySelector('.product-card-variants-column');
      if (!variantsColumn) {
        allUpdated = false;
        return;
      }

      // Clear existing variants
      const variantsDiv = variantsColumn.querySelector('.product-card-variants');
      if (!variantsDiv) {
        allUpdated = false;
        return;
      }

      // Re-render only the variants for this product (preserves color swatches)
      variantsDiv.innerHTML = '';
      this.renderProductVariants(bd, variantsDiv);
    });

    return allUpdated;
  }

  /**
   * Optimized method to update variants for a single product when only color changes
   * This avoids full recalculation and re-rendering of all products
   */
  updateProductVariants(productHandle) {
    const normalizedHandle = this.normalizeHandle(productHandle);

    // Find the item and breakdown for this product
    const item = this.items.find((i) => this.normalizeHandle(i.data.handle) === normalizedHandle);
    const breakdown = this.breakdowns.find((bd) => this.normalizeHandle(bd.handle) === normalizedHandle);

    if (!item || !breakdown) {
      this.updateCalculations();
      return;
    }

    // Get the area (needed for variant matching)
    const area = this.getArea();

    // Recalculate buckets for this product only (preserve counts)
    const manualCounts = new Map();
    breakdown.buckets.forEach((bucket) => {
      const key = `${breakdown.handle}:${bucket.size}:${bucket.variantId || ''}`;
      manualCounts.set(key, bucket.count);
    });

    // Recalculate buckets
    let buckets = [];
    const hasMultiplier = item.data.multiplier && item.data.multiplier > 0;

    if (hasMultiplier || ['geo-textile', 'seam-tape', 'brush-roller'].includes(normalizedHandle)) {
      buckets = this.calculateVariants(item.data, area);
    } else {
      buckets = this.calculateBuckets(item.data, area, true);
    }

    // Re-match variants with the new color
    buckets = this.matchVariantsWithColors(buckets, item, normalizedHandle);

    // Restore manually adjusted counts
    buckets.forEach((bucket) => {
      const key = `${breakdown.handle}:${bucket.size}:${bucket.variantId || ''}`;
      if (manualCounts.has(key)) {
        bucket.count = manualCounts.get(key);
      }
    });

    // Update the breakdown
    breakdown.buckets = buckets;

    // Update only this product's DOM element (not the entire breakdown)
    this.updateSingleProductBreakdown(breakdown);

    // Update pricing (needs full calculation, but that's fine)
    this.updatePricing();
  }

  /**
   * Update product image when color changes for thermal coating
   */
  updateProductImageForColor(productHandle, colorValue) {
    const normalizedHandle = this.normalizeHandle(productHandle);

    // Find the product item
    const item = this.items.find((i) => this.normalizeHandle(i.data.handle) === normalizedHandle);

    // Only update images for products with color variants
    if (!item?.data?.hasColorVariants) {
      return;
    }
    if (!item?.data?.variants) {
      return;
    }

    // Find variant with matching color (case-insensitive)
    const colorValueLower = (colorValue || '').toString().trim().toLowerCase();
    const matchingVariant = item.data.variants.find((v) => {
      const variantColor = (v.color || '').toString().trim().toLowerCase();
      return variantColor === colorValueLower;
    });

    // If no exact match, try to find first variant with the color in the value
    let variant = matchingVariant;
    if (!variant) {
      variant = item.data.variants.find((v) => {
        const variantColor = (v.color || '').toString().trim().toLowerCase();
        return variantColor.includes(colorValueLower) || colorValueLower.includes(variantColor);
      });
    }

    // Get image URL from variant or use first variant's image or product featured image
    let imageUrl = '/placeholder.png';
    if (variant?.imageUrl) {
      imageUrl = variant.imageUrl;
    } else if (variant?.image) {
      imageUrl = variant.image;
    } else if (item.data.variants.length > 0 && item.data.variants[0]?.imageUrl) {
      imageUrl = item.data.variants[0].imageUrl;
    } else if (item.data.featured_image) {
      imageUrl = item.data.featured_image;
    }

    // Find and update the product image element
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const productCard = document.querySelector(
      `#product-breakdown-${sectionIdOnly} .product-card[data-handle="${item.data.handle}"]`
    );

    if (productCard) {
      // Update main product image
      const img = productCard.querySelector('.product-card-image');
      if (img && img.src !== imageUrl) {
        img.src = imageUrl;
      }

      // Update variant images in the variant container
      // Use setTimeout to ensure DOM is updated after updateProductVariants completes
      setTimeout(() => {
        const variantImages = productCard.querySelectorAll('.variant-image');
        variantImages.forEach((variantImg) => {
          const variantItem = variantImg.closest('.variant-item');
          if (variantItem) {
            const variantLabel = variantItem.querySelector('.variant-label');
            if (variantLabel) {
              const labelText = variantLabel.textContent.trim();
              // Try to extract size from label (e.g., "4L (8m²) / Evening Haze", "4L", or "1kg")
              // Match both "L" (liters) and "kg" (kilograms) - treat kg the same as L
              const sizeMatch = /(\d+)(?:L|kg)/i.exec(labelText);
              if (sizeMatch) {
                const size = Number.parseInt(sizeMatch[1], 10);
                // Find variant with matching size and color
                const matchingVariant = item.data.variants.find((v) => {
                  const variantSize = typeof v.size === 'number' ? v.size : Number.parseInt(v.size, 10);
                  const variantColor = (v.color || '').toString().trim().toLowerCase();
                  return variantSize === size && variantColor === colorValueLower;
                });

                if (matchingVariant) {
                  const variantImageUrl = matchingVariant.imageUrl || matchingVariant.image || imageUrl;
                  // Only update if the URL is different to avoid unnecessary reloads
                  if (variantImageUrl && variantImageUrl !== '/placeholder.png' && variantImg.src !== variantImageUrl) {
                    variantImg.src = variantImageUrl;
                  }
                } else {
                  // If no exact match, try to find by size only and use that variant's image
                  const sizeOnlyVariant = item.data.variants.find((v) => {
                    const variantSize = typeof v.size === 'number' ? v.size : Number.parseInt(v.size, 10);
                    return variantSize === size;
                  });
                  if (sizeOnlyVariant?.imageUrl && variantImg.src !== sizeOnlyVariant.imageUrl) {
                    variantImg.src = sizeOnlyVariant.imageUrl;
                  }
                }
              }
            }
          }
        });
      }, 0);
    }
  }

  /**
   * Update a single product's breakdown in the DOM without re-rendering everything
   */
  updateSingleProductBreakdown(breakdown) {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const productCard = document.querySelector(
      `#product-breakdown-${sectionIdOnly} .product-card[data-handle="${breakdown.handle}"]`
    );

    if (!productCard) {
      // If product card doesn't exist, fallback to full render
      this.renderProductBreakdown();
      return;
    }

    // Find the variants column
    const variantsColumn = productCard.querySelector('.product-card-variants-column');
    if (!variantsColumn) {
      // If variants column doesn't exist, fallback to full render
      this.renderProductBreakdown();
      return;
    }

    // Clear existing variants
    const variantsDiv = variantsColumn.querySelector('.product-card-variants');
    if (variantsDiv) {
      variantsDiv.innerHTML = '';
    } else {
      // If variants div doesn't exist, fallback to full render
      this.renderProductBreakdown();
      return;
    }

    // Re-render variants for this product only
    this.renderProductVariants(breakdown, variantsDiv);
  }

  /**
   * Create the updateAllSwatchSelection function for a specific swatches container
   * @param {HTMLElement} swatchesContainer - The swatches container element
   * @param {HTMLElement|null} dropdown - The dropdown element (if exists)
   * @returns {Function} The update function
   */
  createUpdateAllSwatchSelectionFunction(swatchesContainer, dropdown) {
    return () => {
      // Find the checked input from ALL swatches (visible and hidden) - only in this container
      const allSwatchInputs = swatchesContainer.querySelectorAll('input[type="radio"]');
      let selectedInput = null;
      let selectedColorValue = null;

      allSwatchInputs.forEach((input) => {
        if (input.checked) {
          selectedInput = input;
          selectedColorValue = input.value;
        }
      });

      // Update all visible swatches - only within this specific container
      swatchesContainer.querySelectorAll('.custom-color-swatch').forEach((swatch) => {
        const swatchInput = swatch.querySelector('input[type="radio"]');
        const swatchLabel = swatch.querySelector('.custom-color-swatch__label');
        const isSelected = swatchInput && swatchInput === selectedInput;
        this.updateSwatchSelectionState(swatch, swatchLabel, isSelected);
      });

      // Update all dropdown rows (only if dropdown exists)
      if (dropdown) {
        dropdown.querySelectorAll('.color-swatches-dropdown-row').forEach((row) => {
          const rowInput = row.querySelector('input[type="radio"]');
          const swatch = row.querySelector('.custom-color-swatch');
          const label = swatch ? swatch.querySelector('.custom-color-swatch__label') : null;

          // Always remove selected classes first
          row.classList.remove('color-swatches-dropdown-row--selected');
          if (swatch) {
            swatch.classList.remove('custom-color-swatch--selected');
          }
          if (label) {
            label.classList.remove('custom-color-swatch--selected');
          }

          // Add selected classes if this row matches the selected color value
          if (selectedColorValue && rowInput && rowInput.value === selectedColorValue) {
            row.classList.add('color-swatches-dropdown-row--selected');
            if (swatch) {
              swatch.classList.add('custom-color-swatch--selected');
            }
            if (label) {
              label.classList.add('custom-color-swatch--selected');
            }
          }
        });
      }
    };
  }

  /**
   * Close color dropdown (display none + update button). Used when dropdown lives inside show-more container (LRIDY-style).
   * @param {HTMLElement} dropdown - The dropdown element
   * @param {HTMLElement} showMoreButton - The Show more button
   */
  closeColorDropdown(dropdown, showMoreButton) {
    if (!dropdown) return;
    dropdown.style.display = 'none';
    if (showMoreButton) {
      showMoreButton.setAttribute('aria-expanded', 'false');
      showMoreButton.textContent = 'Show more';
    }
  }

  /**
   * Handle dropdown row click for color selection
   * @param {Object} options - Configuration options
   * @param {HTMLElement} options.dropdownRow - The dropdown row element
   * @param {HTMLElement} options.clonedSwatch - The cloned swatch element
   * @param {HTMLElement} options.swatch - The original swatch element
   * @param {HTMLElement} options.swatchesContainer - The swatches container
   * @param {HTMLElement} options.dropdown - The dropdown element
   * @param {HTMLElement} options.showMoreButton - The show more button
   * @param {string} options.productHandle - The product handle
   * @param {Function} options.updateAllSwatchSelection - Function to update swatch selection
   */
  handleDropdownRowClick(options) {
    const {
      dropdownRow,
      clonedSwatch,
      swatch,
      swatchesContainer,
      dropdown,
      showMoreButton,
      productHandle,
      updateAllSwatchSelection,
    } = options;
    dropdownRow.addEventListener('click', (e) => {
      e.stopPropagation();
      const input = clonedSwatch.querySelector('input[type="radio"]');
      if (input) {
        // Get color value from the input's value attribute (most reliable)
        const originalInput = swatch.querySelector('input[type="radio"]');
        const colorValue =
          originalInput?.value || input.value || swatch.dataset.value || swatch.getAttribute('title') || '';
        const normalizedHandle = this.normalizeHandle(productHandle);

        // Update selected color
        if (colorValue) {
          this.selectedColors[normalizedHandle] = colorValue;
        }

        // Find and check the corresponding visible swatch input (by color value, not variant ID)
        const visibleInput = this.findVisibleInputByColor(swatchesContainer, colorValue, input.dataset.variantId);

        if (visibleInput) {
          this.checkInputAndUncheckOthers(swatchesContainer, visibleInput);
        } else {
          // If not in visible set, check the original hidden one
          this.checkInputAndUncheckOthers(swatchesContainer, originalInput);
        }

        // Update visual selection state
        updateAllSwatchSelection();

        // Optimize: Only update variants for this specific product
        this.updateProductVariants(productHandle);

        this.updateProductImageForColor(productHandle, colorValue);

        // Close dropdown after selection
        this.closeColorDropdown(dropdown, showMoreButton);
      }
    });
  }

  /**
   * Handle color selection from a swatch click
   * @param {Event} e - The click event
   * @param {Object} options - Configuration options
   * @param {HTMLElement} options.input - The radio input element
   * @param {HTMLElement} options.swatch - The swatch element
   * @param {HTMLElement} options.swatchesContainer - The swatches container
   * @param {string} options.productHandle - The product handle
   * @param {Function} options.updateColorLabels - Function to update color labels
   */
  handleSwatchColorSelection(e, options) {
    const { input, swatch, swatchesContainer, productHandle, updateColorLabels } = options;
    // Don't interfere if clicking directly on the input (let native behavior handle it)
    if (e.target.type === 'radio' || e.target.tagName === 'INPUT') {
      return;
    }

    // Only stop propagation on click, not on hover - this preserves tooltip functionality
    if (e.type === 'click') {
      e.stopPropagation();
    }

    // Uncheck all other inputs in the container first
    swatchesContainer.querySelectorAll('input[type="radio"]').forEach((inp) => {
      if (inp !== input) {
        inp.checked = false;
      }
    });

    // Check the input
    input.checked = true;

    // Get color value before triggering change
    const colorValue = input.value || swatch.dataset.value || swatch.getAttribute('title') || '';
    const normalizedHandle = this.normalizeHandle(productHandle);

    // Manually trigger change event immediately
    // Create a synthetic change event that will bubble properly
    const changeEvent = new Event('change', {
      bubbles: true,
      cancelable: true,
      composed: true,
    });

    // Dispatch the event directly on the input
    // Use requestAnimationFrame to ensure DOM is updated first
    requestAnimationFrame(() => {
      input.dispatchEvent(changeEvent);

      // Also directly call the update logic as a backup
      if (colorValue) {
        this.selectedColors[normalizedHandle] = colorValue;
        updateColorLabels(colorValue);
        this.updateProductVariants(productHandle);
        // Update image when color changes (any product with color variants)
        this.updateProductImageForColor(productHandle, colorValue);
      }
    });

    // Also dispatch after a short delay as backup to ensure it's caught
    setTimeout(() => {
      // Double-check the input is still checked (in case something unchecks it)
      if (input.checked) {
        const backupEvent = new Event('change', {
          bubbles: true,
          cancelable: true,
          composed: true,
        });
        input.dispatchEvent(backupEvent);
      }
    }, 50);
  }

  /**
   * Set up change listener for color swatches
   * @param {HTMLElement} swatchesContainer - The swatches container
   * @param {string} productHandle - The product handle
   * @param {Function} updateAllSwatchSelection - Function to update swatch selection
   * @param {Function} updateColorLabels - Function to update color labels
   */
  setupSwatchChangeListener(
    swatchesContainer,
    productHandle,
    updateAllSwatchSelection,
    updateColorLabels
  ) {
    swatchesContainer.addEventListener(
      'change',
      (e) => {
        if (e.target.type === 'radio') {
          // Stop propagation to prevent global handlers from interfering
          e.stopPropagation();

          // Update visual selection state first (only for this product's swatches)
          updateAllSwatchSelection();

          // Update variant selection when color changes
          const selectedInput = e.target;
          const selectedSwatch = selectedInput.closest('.custom-color-swatch');
          if (selectedSwatch) {
            // Get color value from the input's value attribute (most reliable)
            // The input value should match the variant color exactly
            const colorValue =
              selectedInput.value || selectedSwatch.dataset.value || selectedSwatch.getAttribute('title') || '';
            const normalizedHandle = this.normalizeHandle(productHandle);

            if (colorValue) {
              this.selectedColors[normalizedHandle] = colorValue;
              // Update color labels
              updateColorLabels(colorValue);

              // Optimize: Only update variants for this specific product, not full recalculation
              this.updateProductVariants(productHandle);

              // Update product and variant images (handled by updateProductImageForColor)
              this.updateProductImageForColor(productHandle, colorValue);
            }
          }
        }
      },
      true
    ); // Use capture phase to handle before other listeners
  }

  /**
   * Render variants for a single product (extracted from renderProductBreakdown)
   */
  renderProductVariants(breakdown, variantsDiv) {
    if (!breakdown.buckets || breakdown.buckets.length === 0) {
      return;
    }

    breakdown.buckets.forEach((bucket) => {
      const variantItem = document.createElement('div');
      variantItem.className = 'variant-item';

      const img = document.createElement('img');
      img.src = bucket.imageUrl || '/placeholder.png';
      img.alt = bucket.label || '';
      img.className = 'variant-image';

      const variantInfo = document.createElement('div');
      variantInfo.className = 'variant-info';

      const label = document.createElement('div');
      label.className = 'variant-label';
      label.textContent = bucket.label || `${this.formatDecimal(bucket.size)}L`;

      const price = document.createElement('div');
      price.className = 'variant-price';
      price.textContent = `AUD ${(bucket.price || 0).toFixed(2)}`;

      variantInfo.appendChild(label);
      variantInfo.appendChild(price);

      const countDiv = document.createElement('div');
      countDiv.className = 'variant-count';

      const countInput = document.createElement('input');
      countInput.type = 'number';
      countInput.className = 'variant-count-input';
      countInput.value = bucket.count;
      countInput.min = 0;

      const decreaseBtn = document.createElement('button');
      decreaseBtn.className = 'variant-count-btn';
      decreaseBtn.textContent = '-';
      decreaseBtn.addEventListener('click', () => {
        if (bucket.count > 0) {
          bucket.count--;
          countInput.value = bucket.count;
          // Mark this bucket as manually adjusted
          const key = `${breakdown.handle}:${bucket.variantId || ''}`;
          this.manuallyAdjustedBuckets.add(key);
          this.updateCalculations(); // Full recalculation needed for quantity changes
        }
      });

      countInput.addEventListener('change', (e) => {
        bucket.count = Math.max(0, Number.parseInt(e.target.value, 10) || 0);
        countInput.value = bucket.count;
        // Mark this bucket as manually adjusted
        const key = `${breakdown.handle}:${bucket.variantId || ''}`;
        this.manuallyAdjustedBuckets.add(key);
        this.updateCalculations(); // Full recalculation needed for quantity changes
      });

      const increaseBtn = document.createElement('button');
      increaseBtn.className = 'variant-count-btn';
      increaseBtn.textContent = '+';
      increaseBtn.addEventListener('click', () => {
        bucket.count++;
        countInput.value = bucket.count;
        // Mark this bucket as manually adjusted
        const key = `${breakdown.handle}:${bucket.variantId || ''}`;
        this.manuallyAdjustedBuckets.add(key);
        this.updateCalculations(); // Full recalculation needed for quantity changes
      });

      countDiv.appendChild(decreaseBtn);
      countDiv.appendChild(countInput);
      countDiv.appendChild(increaseBtn);

      variantItem.appendChild(img);
      variantItem.appendChild(variantInfo);
      variantItem.appendChild(countDiv);
      variantsDiv.appendChild(variantItem);
    });
  }

  updateLengthDisplay() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const lengthDisplay = document.querySelector(`${sectionPrefix} .calculator-length-display-${sectionIdOnly}`);
    if (lengthDisplay) {
      lengthDisplay.textContent = `${this.length}m`;
    }
  }

  updateAreaDisplay() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const areaDisplay = document.querySelector(`${sectionPrefix} #calculated-area-${sectionIdOnly}`);
    if (areaDisplay) {
      const area = this.getArea();
      areaDisplay.textContent = area.toFixed(1);
    }
  }

  calculateItemBreakdown(item, area) {
    const handle = this.normalizeHandle(item.data.handle);
    const useFull = this.fullCover; // Use the full cover toggle state

    let buckets = [];
    let defaultLen = null;

    // rapid-cure-spray is calculated separately based on sealant litres in addRapidCureSprayIfNeeded()
    // Return empty buckets here - it will be added back after sealant is calculated
    if (handle === 'rapid-cure-spray') {
      return {
        handle,
        name: item.data.name,
        buckets: [],
        selected: item.selected,
        data: item.data,
        defaultLength: null,
      };
    }

    // Check if product has a multiplier (length-based product)
    const hasMultiplier = item.data.multiplier && item.data.multiplier > 0;

    if (hasMultiplier || ['geo-textile', 'seam-tape', 'brush-roller'].includes(handle)) {
      buckets = this.calculateVariants(item.data, area);
      if (hasMultiplier || handle === 'geo-textile') {
        // defaultLen for reinforcing tape / geo-textile: base litres (sealant or coating) × multiplier
        let baseItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'waterproof-sealant');
        if (!baseItem) baseItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'protective-top-coat');
        if (baseItem) {
          const baseHandle = this.normalizeHandle(baseItem.data.handle);
          let baseLitres = 0;
          if (this.concreteRoof && baseItem.data.coverageRough && baseItem.data.coverageRough > 0) {
            const areaPercentage = baseItem.data.areaPercentage || 1;
            const effectiveAreaPercentage = useFull ? 1 : areaPercentage;
            const effectiveArea = Number(area) * Number(effectiveAreaPercentage);
            baseLitres = effectiveArea * Number(baseItem.data.coverageRough);
            baseLitres = Math.round(baseLitres * 1000) / 1000;
          } else if (this.coverageRates[baseHandle]) {
            baseLitres = this.coverageRates[baseHandle](area, useFull);
          }
          defaultLen = Math.round(baseLitres * (item.data.multiplier || 1));
        } else {
          defaultLen = 0;
        }
      }
    } else {
      buckets = this.calculateBuckets(item.data, area, useFull);
    }

    // Match variants with selected colors
    buckets = this.matchVariantsWithColors(buckets, item, handle);

    return {
      handle,
      name: (() => {
        if (handle === 'waterproof-sealant') {
          return item.data.name.replace(/Liquid Rubber DIY\s*/i, '').trim();
        }
        if (handle === 'etch-primer') {
          return item.data.name
            .replace(/Liquid Rubber DIY\s*/i, '')
            .replace(/\s*-\s*Metal Surfaces/i, '')
            .trim();
        }
        return item.data.name;
      })(),
      buckets,
      selected: item.selected,
      data: item.data,
      defaultLength: defaultLen,
    };
  }

  matchVariantsWithColors(buckets, item, handle) {
    return buckets.map((b) => {
      const wanted = this.selectedColors[handle];
      // Normalize color values for comparison (case-insensitive, trimmed)
      const normalizeColor = (color) => (color || '').toString().trim().toLowerCase();
      const wantedNormalized = wanted ? normalizeColor(wanted) : null;

      // Helper to parse numeric size from variant (handles "4L", "4kg", "4", 4, etc.)
      const parseVariantSize = (variant) => {
        if (typeof variant.size === 'number') return variant.size;
        if (typeof variant.size === 'string') {
          const match = variant.size.match(/(\d+\.?\d*)/);
          return match ? Number.parseFloat(match[1]) : null;
        }
        // Try to parse from label/title - match both "L" (liters) and "kg" (kilograms)
        const label = variant.label || variant.title || '';
        const match = label.match(/(\d+\.?\d*)\s*(?:L|kg)/i);
        return match ? Number.parseFloat(match[1]) : null;
      };

      // Try to find variant matching both size and color
      let variant = wantedNormalized
        ? item.data.variants.find((v) => {
            const variantColorNormalized = normalizeColor(v.color);
            const variantSize = parseVariantSize(v);
            const sizeMatches = variantSize !== null && Math.abs(variantSize - b.size) < 0.01; // Allow small floating point differences
            const colorMatches = variantColorNormalized === wantedNormalized;
            return sizeMatches && colorMatches;
          })
        : undefined;

      // Fallback: try by variantId, then by size only, then by label
      variant =
        variant ||
        (b.variantId ? item.data.variants.find((v) => v.variantId === b.variantId) : undefined) ||
        item.data.variants.find((v) => {
          const variantSize = parseVariantSize(v);
          return variantSize !== null && Math.abs(variantSize - b.size) < 0.01;
        }) ||
        (handle === 'brush-roller'
          ? item.data.variants.find((v) => (v.label || '').trim() === (b.label || '').trim())
          : undefined);

      if (!variant) {
        return { ...b, price: 0, imageUrl: '/placeholder.png', variantId: '' };
      }
      // IMPORTANT: Use b.size (numeric from calculation) not variant.size (string from product data)
      // variant.size is the variant title like "4L", but b.size is the parsed number like 4
      return {
        ...variant,
        size: b.size, // Keep the numeric size from the bucket calculation
        imageUrl: b.imageUrl || variant.imageUrl,
        count: b.count, // Keep the count from the bucket calculation
      };
    });
  }

  addRapidCureSprayIfNeeded(prelim) {
    const list = prelim.slice();
    const sprayItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'rapid-cure-spray');

    if (!sprayItem) {
      return list; // Store doesn't have rapid-cure-spray - this is a valid config
    }

    const sprayBuckets = this.calculateRapidCureSpray(sprayItem, prelim);
    list.push({
      handle: 'rapid-cure-spray',
      name: sprayItem.data.name,
      buckets: sprayBuckets,
      selected: sprayItem.selected,
      data: sprayItem.data,
      defaultLength: null,
    });

    return list;
  }

  calculateRapidCureSpray(sprayItem, prelim) {
    // Ensure sealant is calculated first - find it in prelim
    const sealantForSpray = prelim.find((b) => this.normalizeHandle(b.handle) === 'waterproof-sealant');

    if (!sealantForSpray) {
      return [];
    }

    if (!sealantForSpray.buckets || sealantForSpray.buckets.length === 0) {
      return [];
    }

    // Calculate total sealant litres sold
    const sprayLitresSold = sealantForSpray.buckets.reduce((sum, bk) => {
      const size = Number(bk.size) || 0;
      const count = Number(bk.count) || 0;
      return sum + size * count;
    }, 0);

    if (sprayLitresSold <= 0) {
      return [];
    }

    // Rapid cure spray is calculated as a direct ratio to sealant litres
    // Default: 0.02L of rapid cure spray per 1L of waterproof sealant
    // Formula: spray_litres = sealant_litres * coverageRate
    // Parse coverage as number (might come as string from Liquid)
    const coverageRaw = sprayItem.data.coverage;
    const coverageRate =
      coverageRaw !== undefined && coverageRaw !== null && Number(coverageRaw) > 0 ? Number(coverageRaw) : 0.02;
    const eps = 1e-6; // Epsilon for floating point comparisons
    const required = Math.ceil(sprayLitresSold * coverageRate * 100) / 100;

    if (required <= eps) {
      return [];
    }

    if (!sprayItem.data.variants || sprayItem.data.variants.length === 0) {
      return [];
    }

    const variantsWithSize = this.parseSprayVariants(sprayItem.data.variants);
    if (variantsWithSize.length === 0) {
      return [];
    }

    return this.calculateSprayBuckets(required, variantsWithSize);
  }

  parseSprayVariants(variants) {
    // Expected sizes in order - used for normalization
    const expectedSizes = [0.2, 0.5, 1, 5, 20];
    const eps = 1e-3; // Tolerance for size matching (0.001L)

    const variantsWithSize = variants
      .map((v) => {
        // Try label first, then title, then size (which might be a string)
        const label = v.label || v.title || (typeof v.size === 'string' ? v.size : '') || '';

        // Try multiple regex patterns to match different formats:
        // - "0.2L", "0.2 L", "0.2L Bottle", etc.
        // - "0.2", "0.2 litres", etc.
        let m = /(\d+(?:\.\d+)?)\s*L/i.exec(label);
        if (!m) {
          // Try without L
          m = /^(\d+(?:\.\d+)?)\s*(?:litres?|ml|mL)?$/i.exec(label.trim());
        }
        if (!m) {
          // Try extracting just the number at the start
          m = /^(\d+(?:\.\d+)?)/.exec(label.trim());
        }

        if (!m) {
          return null;
        }
        const numericSize = Number.parseFloat(m[1]);

        // Normalize size to match expected sizes (handle floating point precision issues)
        // Find the closest expected size within tolerance
        let normalizedSize = numericSize;
        for (const expectedSize of expectedSizes) {
          if (Math.abs(numericSize - expectedSize) < eps) {
            normalizedSize = expectedSize;
            break;
          }
        }

        return { ...v, size: normalizedSize };
      })
      .filter((v) => v !== null && !Number.isNaN(v.size));
    return variantsWithSize;
  }

  calculateSprayBuckets(required, variantsWithSize) {
    if (!variantsWithSize || variantsWithSize.length === 0) {
      return [];
    }

    const byExactSize = new Map();
    variantsWithSize.forEach((v) => {
      const s = v.size;
      if (!byExactSize.has(s)) {
        byExactSize.set(s, v);
      }
    });

    const sizesOrder = [0.2, 0.5, 1, 5, 20];
    const counts = { 0.2: 0, 0.5: 0, 1: 0, 5: 0, 20: 0 };
    let remaining = required;
    const eps = 1e-6;

    // Helper to add a bottle size if available; otherwise upgrade to next available size
    const addSize = (target) => {
      let idx = sizesOrder.indexOf(target);
      // Find the first available size >= target
      while (idx < sizesOrder.length && !byExactSize.has(sizesOrder[idx])) {
        idx++;
      }
      // If no size found, use the largest available size
      if (idx >= sizesOrder.length) {
        const availableSizes = sizesOrder.filter((s) => byExactSize.has(s));
        if (availableSizes.length === 0) {
          return;
        }
        const chosen = Math.max(...availableSizes);
        counts[chosen] = (counts[chosen] || 0) + 1;
      } else {
        const chosen = sizesOrder[idx];
        // Only add if the size exists in byExactSize
        if (byExactSize.has(chosen)) {
          counts[chosen] = (counts[chosen] || 0) + 1;
        }
      }
    };

    // Use 20L only when smaller-bottle constraints cannot satisfy the remainder.
    // Max without a 20L is: 3×5L + 4×1L + 0.5L + 0.2L = 19.7L
    while (remaining > 19.7 + eps) {
      addSize(20);
      remaining -= 20;
    }

    // Use 5L while remainder exceeds what smaller sizes can cover: 4×1L + 0.5 + 0.2 = 4.7L
    while (remaining > 4.7 + eps) {
      addSize(5);
      remaining -= 5;
    }

    // Use up to four 1L bottles while remainder > 0.7L
    let onesUsed = 0;
    while (remaining > 0.7 + eps && onesUsed < 4) {
      addSize(1);
      onesUsed++;
      remaining -= 1;
    }

    // Final fractional remainder rules
    if (remaining > eps) {
      if (remaining <= 0.2 + eps) {
        addSize(0.2);
      } else if (remaining <= 0.5 + eps) {
        addSize(0.5);
      } else {
        // 0.5L + 0.2L, but never two 0.2L
        addSize(0.5);
        addSize(0.2);
      }
    }

    // Collapse counts into buckets using the chosen variant per size
    const sprayBuckets = sizesOrder
      .filter((s) => {
        const hasCount = (counts[s] || 0) > 0;
        const hasVariant = byExactSize.has(s);
        return hasCount && hasVariant;
      })
      .map((s) => {
        const v = byExactSize.get(s);
        if (!v) {
          return null;
        }
        // Try to find variant with matching color if color is selected
        const wanted = this.selectedColors['rapid-cure-spray'];
        let variant = v;
        if (wanted) {
          const colorMatch = variantsWithSize.find((v) => v.size === s && v.color === wanted);
          if (colorMatch) variant = colorMatch;
        }
        // Create bucket structure matching calculateBuckets() format
        // Use s (the size from sizesOrder) as the definitive size
        const bucket = {
          size: s, // Use the size from sizesOrder, not variant.size (which might be string or number)
          count: counts[s],
          price: variant.price || 0,
          variantId: variant.variantId || variant.id || '',
          imageUrl: variant.imageUrl || variant.image || '/placeholder.png',
          label: variant.label || variant.title || `${s}L Bottle`,
          color: variant.color || '',
        };
        return bucket;
      })
      .filter(Boolean);

    return sprayBuckets;
  }

  organizeBreakdowns(list) {
    // Ensure brush-roller is last
    // Exclude liquid-rubber-diy-2-piece-brush-kit (it's in bonus, not calculator)
    // Include rapid-cure-spray, sealer, and etch-primer even if they have no buckets
    // (sealer and etch-primer need to be always available for toggling)
    // Also include products with multipliers (length-based products like Grit) even if they have no buckets
    // Products with multiplier property (even if 0) are configured products that should always show
    const withBuckets = list.filter((b) => {
      const hasBuckets = b.buckets && b.buckets.length > 0;
      const isSpecialProduct =
        (b.handle === 'rapid-cure-spray' && this.hasProductInKit('rapid-cure-spray')) ||
        (b.handle === 'sealer' && this.hasProductInKit('sealer')) ||
        (b.handle === 'etch-primer' && this.hasProductInKit('etch-primer'));
      const hasMultiplier = b.data && 'multiplier' in b.data;
      return (hasBuckets || isSpecialProduct || hasMultiplier) && b.handle !== 'liquid-rubber-diy-2-piece-brush-kit';
    });
    const nonBonus = withBuckets.filter((b) => b.handle !== 'brush-roller');
    const bonus = withBuckets.filter((b) => b.handle === 'brush-roller');
    return nonBonus.concat(bonus);
  }

  /**
   * Preserve color swatches selected state before clearing container
   * @param {HTMLElement} container - The product breakdown container
   * @returns {Map<string, string>} Map of handle to selected color value
   */
  preserveColorSwatches(container) {
    const preservedSelectedColors = new Map();
    const existingProductCards = Array.from(container.querySelectorAll('.product-card'));

    existingProductCards.forEach((card) => {
      const handle = card.dataset.handle;
      if (handle) {
        const swatchesContainer = card.querySelector('.product-color-swatches-container');
        if (swatchesContainer) {
          const selectedInput = swatchesContainer.querySelector('input[type="radio"]:checked');
          const selectedColor = selectedInput ? selectedInput.value : null;
          if (selectedColor) {
            preservedSelectedColors.set(handle, selectedColor);
          }
        }
      }
    });

    return preservedSelectedColors;
  }

  /**
   * Set up click-outside handler for tooltips
   */
  setupTooltipClickHandler() {
    if (this.tooltipClickHandler) return;

    this.tooltipClickHandler = (e) => {
      const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
      const allTooltipContainers = document.querySelectorAll(
        `${sectionPrefix} .product-why-tooltip-container, ${sectionPrefix} .toggle-tooltip-container`
      );
      allTooltipContainers.forEach((tooltipContainer) => {
        if (!tooltipContainer.contains(e.target)) {
          tooltipContainer.classList.remove('active');
        }
      });
    };
    document.addEventListener('click', this.tooltipClickHandler, true);
  }

  /**
   * Create product image column with clickable image wrapper
   * @param {Object} bd - Breakdown data
   * @returns {HTMLElement} Product column element
   */
  createProductImageColumn(bd) {
    const productColumn = document.createElement('div');
    productColumn.className = 'product-card-column';

    const imageUrl =
      bd.buckets.length > 0
        ? bd.buckets[0].imageUrl || '/placeholder.png'
        : bd.data.featured_image || '/placeholder.png';

    const imageWrapper = document.createElement('button');
    imageWrapper.type = 'button';
    imageWrapper.className = 'product-card-image-wrapper';
    imageWrapper.setAttribute('aria-label', `View ${bd.name} options`);
    imageWrapper.dataset.productHandle = bd.data.handle;
    imageWrapper.dataset.productId = bd.data.id;
    imageWrapper.dataset.productUrl = `/products/${bd.data.handle}`;

    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = bd.name;
    img.className = 'product-card-image';
    img.dataset.productHandle = bd.handle;
    imageWrapper.appendChild(img);

    const spinner = document.createElement('div');
    spinner.className = 'loading__spinner hidden';
    const existingSpinner = document.querySelector('.loading__spinner');
    if (existingSpinner) {
      spinner.innerHTML = existingSpinner.innerHTML;
    } else {
      spinner.innerHTML =
        '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle></svg>';
    }
    imageWrapper.appendChild(spinner);

    imageWrapper.addEventListener('click', (e) => {
      e.preventDefault();
      this.openQuickAddModal(bd.data.handle, bd.data.id, imageWrapper);
    });

    productColumn.appendChild(imageWrapper);
    return productColumn;
  }

  /**
   * Create "Why do I need?" tooltip if whyText is available
   * @param {Object} bd - Breakdown data
   * @param {HTMLElement} titleColumn - Title column to append tooltip to
   */
  createWhyTooltip(bd, titleColumn) {
    if (!bd.data.whyText || !bd.data.whyText.trim()) return;

    const whyTooltipContainer = document.createElement('div');
    whyTooltipContainer.className = 'product-why-tooltip-container';

    const whyTrigger = document.createElement('button');
    whyTrigger.className = 'product-why-trigger';
    whyTrigger.type = 'button';
    whyTrigger.setAttribute('aria-label', 'Why do I need this product?');
    whyTrigger.innerHTML = 'Why do I need? <span class="why-icon">i</span>';

    const whyTooltip = document.createElement('div');
    whyTooltip.className = 'product-why-tooltip';
    whyTooltip.textContent = bd.data.whyText;

    whyTooltipContainer.appendChild(whyTrigger);
    whyTooltipContainer.appendChild(whyTooltip);

    whyTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isActive = whyTooltipContainer.classList.contains('active');
      const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
      const allTooltipContainers = document.querySelectorAll(`${sectionPrefix} .product-why-tooltip-container`);
      allTooltipContainers.forEach((container) => {
        if (container !== whyTooltipContainer) {
          container.classList.remove('active');
        }
      });
      if (isActive) {
        whyTooltipContainer.classList.remove('active');
      } else {
        whyTooltipContainer.classList.add('active');
      }
    });

    titleColumn.appendChild(whyTooltipContainer);
  }

  /**
   * Create color swatches section for products with color variants
   * This is a complex method that handles color selection UI
   * @param {Object} bd - Breakdown data
   * @param {Map<string, string>} preservedSelectedColors - Map of preserved colors
   * @param {HTMLElement} titleColumn - Title column to append to
   */
  createColorSwatchesSection(bd, preservedSelectedColors, titleColumn) {
    if (!bd.data.hasColorVariants || !bd.data.colorSwatchesHtml) return;

    const normalizedHandle = this.normalizeHandle(bd.handle);
    // Full cover toggle only for sealant when it's in the kit (no hardcoded handle assumptions)
    const isWaterproofSealant = normalizedHandle === 'waterproof-sealant' && this.hasProductInKit('waterproof-sealant');

    const colorSwatchesAndToggleContainer = document.createElement('div');
    colorSwatchesAndToggleContainer.className = 'product-color-swatches-and-toggle-container';
    colorSwatchesAndToggleContainer.style.display = 'flex';
    colorSwatchesAndToggleContainer.style.alignItems = 'flex-start';
    colorSwatchesAndToggleContainer.style.gap = '1.5rem';

    const colorSwatchesWrapper = document.createElement('div');
    colorSwatchesWrapper.className = 'product-color-swatches-wrapper';

    const colorLabelAbove = document.createElement('div');
    colorLabelAbove.className = 'product-color-label-above';
    colorLabelAbove.style.fontWeight = 'bold';
    colorLabelAbove.style.color = '#000000';
    colorLabelAbove.style.marginBottom = '0';
    colorLabelAbove.style.fontSize = '1rem';
    // Generic "Color:" label for all products with color variants (no hardcoded handle checks)
    colorLabelAbove.textContent = 'Color: ';

    const colorSwatchesContainer = document.createElement('div');
    colorSwatchesContainer.className = 'product-color-swatches-container';
    colorSwatchesContainer.innerHTML = bd.data.colorSwatchesHtml;

    const preservedColor = preservedSelectedColors.get(normalizedHandle);
    if (preservedColor) {
      colorSwatchesContainer.dataset.preservedColor = preservedColor;
    }

    const swatchesElement = colorSwatchesContainer.querySelector('.custom-color-swatches');
    if (swatchesElement) {
      swatchesElement.dataset.productId = bd.data.id;
      swatchesElement.dataset.calculatorProductId = `${this.sectionId}-${bd.data.id}`;

      const inputs = colorSwatchesContainer.querySelectorAll('input[type="radio"]');
      const labels = colorSwatchesContainer.querySelectorAll('label');
      const preservedColorValue = colorSwatchesContainer.dataset.preservedColor;
      const selectedColor = preservedColorValue || this.selectedColors[normalizedHandle];

      if (preservedColorValue) {
        this.selectedColors[normalizedHandle] = preservedColorValue;
      }

      const updateColorLabels = (colorName) => {
        if (colorName) {
          const formattedColorName = colorName
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
          colorLabelAbove.textContent = `Color: ${formattedColorName}`;
        } else {
          colorLabelAbove.textContent = 'Color: ';
        }
      };

      let initialColorName = '';
      if (selectedColor) {
        const selectedInput = Array.from(inputs).find((input) => {
          const inputColor = (input.value || '').toString().trim().toLowerCase();
          const selectedColorNormalized = selectedColor.toString().trim().toLowerCase();
          return inputColor === selectedColorNormalized;
        });
        if (selectedInput) {
          initialColorName = selectedInput.value || selectedInput.getAttribute('title') || selectedColor;
        } else {
          initialColorName = selectedColor;
        }
      } else {
        const firstInput = inputs[0];
        if (firstInput) {
          initialColorName = firstInput.value || firstInput.getAttribute('title') || '';
        }
      }
      updateColorLabels(initialColorName);

      inputs.forEach((input, index) => {
        const oldId = input.id;
        const newId = `${oldId}-${this.sectionId}-${bd.data.id}`;
        input.id = newId;
        if (labels[index]) {
          labels[index].setAttribute('for', newId);
        }
        const dataIndex = input.dataset.index || '';
        input.name = `option-${this.sectionId}-${bd.data.id}-${dataIndex}`;

        if (selectedColor) {
          const inputColor = (input.value || '').toString().trim().toLowerCase();
          const selectedColorNormalized = selectedColor.toString().trim().toLowerCase();
          if (inputColor === selectedColorNormalized) {
            input.checked = true;
          } else {
            input.checked = false;
          }
        }
      });

      const swatchesContainer = colorSwatchesContainer.querySelector('.custom-color-swatches__container');
      let dropdown = null;
      let updateAllSwatchSelection = null;

      if (swatchesContainer) {
        const allSwatches = Array.from(swatchesContainer.querySelectorAll('.custom-color-swatch'));
        const maxVisible = 4;

        updateAllSwatchSelection = this.createUpdateAllSwatchSelectionFunction(swatchesContainer, null);

        if (allSwatches.length > maxVisible) {
          allSwatches.forEach((swatch, index) => {
            if (index >= maxVisible) {
              swatch.classList.add('color-swatch-hidden');
              swatch.style.display = 'none';
            }
          });

          /* Wrapper for visible 4 swatches + dropdown so dropdown opens under them at same width */
          const visibleRowWrapper = document.createElement('div');
          visibleRowWrapper.className = 'kit-builder-color-swatches-visible-row';

          const showMoreContainer = document.createElement('div');
          showMoreContainer.className = 'color-swatches-show-more-container';

          const showMoreButton = document.createElement('button');
          showMoreButton.type = 'button';
          showMoreButton.className = 'color-swatches-show-more-btn';
          showMoreButton.textContent = 'Show more';
          showMoreButton.setAttribute('aria-expanded', 'false');
          showMoreButton.setAttribute('aria-label', 'Show more color options');

          dropdown = document.createElement('div');
          dropdown.className = 'color-swatches-dropdown';
          dropdown.style.display = 'none';

          updateAllSwatchSelection = this.createUpdateAllSwatchSelectionFunction(swatchesContainer, dropdown);

          /* Move first 4 swatches into wrapper so dropdown can sit under them */
          allSwatches.slice(0, maxVisible).forEach((sw) => visibleRowWrapper.appendChild(sw));

          const hiddenSwatches = allSwatches.slice(maxVisible);
          hiddenSwatches.forEach((swatch) => {
            const dropdownRow = document.createElement('div');
            dropdownRow.className = 'color-swatches-dropdown-row';

            const clonedSwatch = swatch.cloneNode(true);
            clonedSwatch.style.display = 'inline-block';
            clonedSwatch.classList.remove('color-swatch-hidden', 'custom-color-swatch--selected');
            const clonedLabel = clonedSwatch.querySelector('.custom-color-swatch__label');
            if (clonedLabel) {
              clonedLabel.classList.remove('custom-color-swatch--selected');
            }
            const clonedInput = clonedSwatch.querySelector('input[type="radio"]');
            if (clonedInput) {
              clonedInput.checked = false;
              clonedInput.removeAttribute('checked');
            }

            const colorName = (swatch.getAttribute('title') || swatch.dataset.value || swatch.querySelector('input[type="radio"]')?.value || '').toString().trim();
            const clonedTooltip = clonedSwatch.querySelector('.custom-color-swatch__tooltip');
            if (clonedTooltip) clonedTooltip.remove();
            clonedSwatch.removeAttribute('data-tooltip');

            const colorNameSpan = document.createElement('span');
            colorNameSpan.className = 'color-swatches-dropdown-name';
            colorNameSpan.textContent = colorName;

            dropdownRow.appendChild(clonedSwatch);
            dropdownRow.appendChild(colorNameSpan);

            this.handleDropdownRowClick({
              dropdownRow,
              clonedSwatch,
              swatch,
              swatchesContainer,
              dropdown,
              showMoreButton,
              productHandle: bd.handle,
              updateAllSwatchSelection,
            });

            dropdown.appendChild(dropdownRow);
          });

          /* Append dropdown to show-more container (LRIDY-style): position:absolute from this container, no layout shift, scrolls with page */
          showMoreContainer.appendChild(showMoreButton);
          showMoreContainer.appendChild(dropdown);
          swatchesContainer.insertBefore(visibleRowWrapper, swatchesContainer.firstChild);
          swatchesContainer.appendChild(showMoreContainer);

          showMoreButton.addEventListener('click', (e) => {
            e.stopPropagation();
            const isExpanded = showMoreButton.getAttribute('aria-expanded') === 'true';
            if (isExpanded) {
              this.closeColorDropdown(dropdown, showMoreButton);
            } else {
              dropdown.style.display = 'block';
              showMoreButton.setAttribute('aria-expanded', 'true');
              showMoreButton.textContent = 'Show less';
            }
          });

          document.addEventListener('click', (e) => {
            if (!showMoreContainer.contains(e.target)) {
              this.closeColorDropdown(dropdown, showMoreButton);
            }
          });
        }

        // Only add tooltips to the first 4 visible swatches - not to hidden ones (dropdown shows names)
        const allSwatchesList = Array.from(swatchesContainer.querySelectorAll('.custom-color-swatch'));
        const visibleSwatchesOnly = allSwatchesList.slice(0, maxVisible);
        visibleSwatchesOnly.forEach((swatch) => {
          const input = swatch.querySelector('input[type="radio"]');
          const label = swatch.querySelector('label.custom-color-swatch__label');

          if (!input || input.disabled) return;

          let tooltipText = (input.value || swatch.dataset.value || swatch.getAttribute('title') || '').toString().trim();
          if (tooltipText) {
            // Sanitize: keep only letters, numbers, spaces, hyphens - removes ??, \, |, ' etc
            tooltipText = tooltipText.replace(/[^\w\s-]/g, '').replaceAll(/\s+/g, ' ').trim();
            tooltipText = tooltipText.toUpperCase();
            if (!tooltipText) return;
            // Remove any existing tooltips to prevent duplicates
            swatch.querySelectorAll('.custom-color-swatch__tooltip').forEach((el) => el.remove());
            const tooltipElement = document.createElement('div');
            tooltipElement.className = 'custom-color-swatch__tooltip';
            tooltipElement.textContent = tooltipText;
            swatch.appendChild(tooltipElement);
            swatch.dataset.tooltip = tooltipText;
            swatch.removeAttribute('title');
            if (label) {
              label.removeAttribute('title');
              label.style.pointerEvents = 'auto';
            }
          }

          const handleColorSelection = (e) => {
            this.handleSwatchColorSelection(e, {
              input,
              swatch,
              swatchesContainer,
              productHandle: bd.handle,
              updateColorLabels,
            });
          };

          swatch.addEventListener('click', handleColorSelection, true);
          if (label) {
            label.addEventListener('click', handleColorSelection, true);
          }
        });

        this.setupSwatchChangeListener(
          swatchesContainer,
          bd.handle,
          updateAllSwatchSelection,
          updateColorLabels
        );

        const swatchesElementForEvent = colorSwatchesContainer.querySelector('.custom-color-swatches');
        if (swatchesElementForEvent) {
          swatchesElementForEvent.addEventListener('custom-color-swatch:changed', (e) => {
            const detail = e.detail;
            if (detail?.value) {
              const normalizedHandleForEvent = this.normalizeHandle(bd.handle);
              this.selectedColors[normalizedHandleForEvent] = detail.value;
              updateColorLabels(detail.value);
              this.updateProductVariants(bd.handle);
              this.updateProductImageForColor(bd.handle, detail.value);
            }
          });
        }

        updateAllSwatchSelection();
      }

      colorSwatchesWrapper.appendChild(colorLabelAbove);
      colorSwatchesWrapper.appendChild(colorSwatchesContainer);

      if (this.showRecommendedColorForHandle(bd.data.handle)) {
        const recommendedColorLabel = document.createElement('div');
        recommendedColorLabel.className = 'product-recommended-color';
        recommendedColorLabel.style.fontSize = '0.875rem';
        recommendedColorLabel.style.color = '#6b7280';
        recommendedColorLabel.style.marginTop = '0.5rem';
        recommendedColorLabel.textContent = 'Recommended color: White';
        colorSwatchesWrapper.appendChild(recommendedColorLabel);
      }

      colorSwatchesAndToggleContainer.appendChild(colorSwatchesWrapper);

      if (isWaterproofSealant && this.showFullCoverToggle) {
        const fullCoverToggle = this.createFullCoverToggle();
        colorSwatchesAndToggleContainer.appendChild(fullCoverToggle);
      }

      titleColumn.appendChild(colorSwatchesAndToggleContainer);
    }
  }

  /**
   * Create full cover toggle for waterproof sealant
   * @returns {HTMLElement} Full cover toggle container
   */
  createFullCoverToggle() {
    const fullCoverToggleContainer = document.createElement('div');
    fullCoverToggleContainer.className = 'product-full-cover-toggle-container';
    fullCoverToggleContainer.style.display = 'flex';
    fullCoverToggleContainer.style.flexDirection = 'column';
    fullCoverToggleContainer.style.alignItems = 'flex-start';
    fullCoverToggleContainer.style.gap = '0.5rem';

    const toggleLabelWrapper = document.createElement('div');
    toggleLabelWrapper.className = 'product-full-cover-toggle-label-wrapper';
    toggleLabelWrapper.style.display = 'flex';
    toggleLabelWrapper.style.alignItems = 'center';
    toggleLabelWrapper.style.gap = '0.5rem';

    const toggleLabel = document.createElement('span');
    toggleLabel.className = 'product-full-cover-toggle-label';
    toggleLabel.style.fontSize = '1rem';
    toggleLabel.style.fontWeight = '500';
    toggleLabel.style.color = '#000000';
    toggleLabel.textContent = this.fullCoverToggleLabel;

    toggleLabelWrapper.appendChild(toggleLabel);

    if (this.fullCoverTooltipText) {
      const tooltipContainer = document.createElement('div');
      tooltipContainer.className = 'product-full-cover-toggle-tooltip-container';
      tooltipContainer.style.position = 'relative';
      tooltipContainer.style.display = 'inline-flex';

      const tooltipTrigger = document.createElement('button');
      tooltipTrigger.type = 'button';
      tooltipTrigger.className = 'product-full-cover-toggle-tooltip-trigger';
      tooltipTrigger.setAttribute('aria-label', 'Toggle information');
      tooltipTrigger.style.display = 'inline-flex';
      tooltipTrigger.style.alignItems = 'center';
      tooltipTrigger.style.justifyContent = 'center';
      tooltipTrigger.style.width = '1.125rem';
      tooltipTrigger.style.height = '1.125rem';
      tooltipTrigger.style.padding = '0';
      tooltipTrigger.style.backgroundColor = 'transparent';
      tooltipTrigger.style.border = '1px solid #16a34a';
      tooltipTrigger.style.borderRadius = '50%';
      tooltipTrigger.style.color = '#16a34a';
      tooltipTrigger.style.fontSize = '0.75rem';
      tooltipTrigger.style.fontWeight = '700';
      tooltipTrigger.style.lineHeight = '1';
      tooltipTrigger.style.cursor = 'pointer';
      tooltipTrigger.textContent = 'i';

      const tooltip = document.createElement('div');
      tooltip.className = 'product-full-cover-toggle-tooltip';
      tooltip.textContent = this.fullCoverTooltipText;

      tooltipTrigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isActive = tooltipContainer.classList.contains('active');
        const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
        const allTooltipContainers = document.querySelectorAll(
          `${sectionPrefix} .product-why-tooltip-container, ${sectionPrefix} .product-full-cover-toggle-tooltip-container`
        );
        allTooltipContainers.forEach((container) => {
          if (container !== tooltipContainer) {
            container.classList.remove('active');
          }
        });
        if (isActive) {
          tooltipContainer.classList.remove('active');
        } else {
          tooltipContainer.classList.add('active');
        }
      });

      tooltipContainer.appendChild(tooltipTrigger);
      tooltipContainer.appendChild(tooltip);
      toggleLabelWrapper.appendChild(tooltipContainer);
    }

    const toggleWrapper = document.createElement('label');
    toggleWrapper.className = 'roof-type-toggle';
    toggleWrapper.style.position = 'relative';
    toggleWrapper.style.display = 'inline-block';
    toggleWrapper.style.width = '4rem';
    toggleWrapper.style.height = '2rem';
    toggleWrapper.style.cursor = 'pointer';
    toggleWrapper.setAttribute('for', `full-cover-toggle-${this.sectionIdOnly}`);

    const toggleInput = document.createElement('input');
    toggleInput.type = 'checkbox';
    toggleInput.id = `full-cover-toggle-${this.sectionIdOnly}`;
    toggleInput.className = 'roof-type-toggle-input';
    toggleInput.setAttribute('aria-label', 'Toggle full cover');
    toggleInput.dataset.fullCover = 'false';
    toggleInput.dataset.calculatorId = this.sectionIdOnly;
    toggleInput.checked = this.fullCover;

    toggleInput.addEventListener('change', () => this.handleFullCoverToggle());

    const toggleSlider = document.createElement('span');
    toggleSlider.className = 'roof-type-toggle-slider';

    toggleWrapper.appendChild(toggleInput);
    toggleWrapper.appendChild(toggleSlider);

    fullCoverToggleContainer.appendChild(toggleLabelWrapper);
    fullCoverToggleContainer.appendChild(toggleWrapper);

    return fullCoverToggleContainer;
  }

  renderProductBreakdown() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const container = document.querySelector(`#product-breakdown-${sectionIdOnly}`);
    if (!container) return;

    const preservedSelectedColors = this.preserveColorSwatches(container);
    container.innerHTML = '';
    this.setupTooltipClickHandler();

    this.breakdowns.forEach((bd) => {
      // Always render sealer and etch-primer, but hide them if not visible
      // This ensures they exist in the DOM when toggled
      const shouldRender =
        this.isVisible(bd.handle) ||
        (bd.handle === 'sealer' && this.hasProductInKit('sealer')) ||
        (bd.handle === 'etch-primer' && this.hasProductInKit('etch-primer'));
      if (!shouldRender) return;

      // Show rapid-cure-spray when in kit even if no buckets initially (calculated based on sealant)
      // It will show up once sealant is calculated

      const card = document.createElement('div');
      card.className = 'product-card';
      card.dataset.handle = bd.handle;

      // Hide sealer/etch-primer if not visible (but still render them)
      if (
        (bd.handle === 'sealer' || bd.handle === 'etch-primer') &&
        this.hasProductInKit(bd.handle) &&
        !this.isVisible(bd.handle)
      ) {
        card.style.display = 'none';
      }

      // Always keep products selected
      bd.selected = true;

      // Create two-column layout container
      const cardContent = document.createElement('div');
      cardContent.className = 'product-card-content';

      const productColumn = this.createProductImageColumn(bd);
      cardContent.appendChild(productColumn);

      // Middle column: Product title only
      const titleColumn = document.createElement('div');
      titleColumn.className = 'product-card-title-column';

      const name = document.createElement('div');
      name.className = 'product-card-name';
      name.textContent = bd.name;
      titleColumn.appendChild(name);

      this.createWhyTooltip(bd, titleColumn);

      // Add color swatches if product has color variants
      if (bd.data.hasColorVariants && bd.data.colorSwatchesHtml) {
        const normalizedHandle = this.normalizeHandle(bd.handle);
        const isWaterproofSealant = normalizedHandle === 'waterproof-sealant' && this.hasProductInKit('waterproof-sealant');

        // Create a container for color swatches and full cover toggle (side by side)
        const colorSwatchesAndToggleContainer = document.createElement('div');
        colorSwatchesAndToggleContainer.className = 'product-color-swatches-and-toggle-container';
        colorSwatchesAndToggleContainer.style.display = 'flex';
        colorSwatchesAndToggleContainer.style.alignItems = 'flex-start';
        colorSwatchesAndToggleContainer.style.gap = '1.5rem';

        // Create wrapper for color swatches with text above and below
        const colorSwatchesWrapper = document.createElement('div');
        colorSwatchesWrapper.className = 'product-color-swatches-wrapper';

        const colorLabelAbove = document.createElement('div');
        colorLabelAbove.className = 'product-color-label-above';
        colorLabelAbove.style.fontWeight = 'bold';
        colorLabelAbove.style.color = '#000000';
        colorLabelAbove.style.marginBottom = '0';
        colorLabelAbove.style.fontSize = '1rem';
        colorLabelAbove.textContent = 'Color: ';

        const colorSwatchesContainer = document.createElement('div');
        colorSwatchesContainer.className = 'product-color-swatches-container';

        // Use the HTML from data (swatches will be re-initialized)
        colorSwatchesContainer.innerHTML = bd.data.colorSwatchesHtml;

        // Restore preserved selected color if it exists
        const preservedColor = preservedSelectedColors.get(normalizedHandle);

        // If we have a preserved color, we'll restore it after the swatches are set up
        // Store it for later use
        if (preservedColor) {
          colorSwatchesContainer.dataset.preservedColor = preservedColor;
        }

        // Update product ID in the color swatches to match this calculator instance
        const swatchesElement = colorSwatchesContainer.querySelector('.custom-color-swatches');
        if (swatchesElement) {
          swatchesElement.dataset.productId = bd.data.id;
          swatchesElement.dataset.calculatorProductId = `${this.sectionId}-${bd.data.id}`;

          // Update all input IDs and labels to be unique for this calculator instance
          const inputs = colorSwatchesContainer.querySelectorAll('input[type="radio"]');
          const labels = colorSwatchesContainer.querySelectorAll('label');
          const normalizedHandle = this.normalizeHandle(bd.handle);

          // Use preserved color if available, otherwise use selectedColor from state
          const preservedColor = colorSwatchesContainer.dataset.preservedColor;
          const selectedColor = preservedColor || this.selectedColors[normalizedHandle];

          // Update selectedColors state if we restored a preserved color
          if (preservedColor) {
            this.selectedColors[normalizedHandle] = preservedColor;
          }

          // Function to update color labels with selected color name
          const updateColorLabels = (colorName) => {
            if (colorName) {
              const formattedColorName = colorName
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
              colorLabelAbove.textContent = `Color: ${formattedColorName}`;
            } else {
              colorLabelAbove.textContent = 'Color: ';
            }
          };

          // Get initial selected color name
          let initialColorName = '';
          if (selectedColor) {
            // Find the selected input to get the color name
            const selectedInput = Array.from(inputs).find((input) => {
              const inputColor = (input.value || '').toString().trim().toLowerCase();
              const selectedColorNormalized = selectedColor.toString().trim().toLowerCase();
              return inputColor === selectedColorNormalized;
            });
            if (selectedInput) {
              initialColorName = selectedInput.value || selectedInput.getAttribute('title') || selectedColor;
            } else {
              initialColorName = selectedColor;
            }
          } else {
            // Get first available color as default
            const firstInput = inputs[0];
            if (firstInput) {
              initialColorName = firstInput.value || firstInput.getAttribute('title') || '';
            }
          }
          updateColorLabels(initialColorName);

          inputs.forEach((input, index) => {
            const oldId = input.id;
            const newId = `${oldId}-${this.sectionId}-${bd.data.id}`;
            input.id = newId;
            if (labels[index]) {
              labels[index].setAttribute('for', newId);
            }
            // Update name to be unique per calculator
            const dataIndex = input.dataset.index || '';
            input.name = `option-${this.sectionId}-${bd.data.id}-${dataIndex}`;

            // Set checked state based on selectedColor
            if (selectedColor) {
              const inputColor = (input.value || '').toString().trim().toLowerCase();
              const selectedColorNormalized = selectedColor.toString().trim().toLowerCase();
              if (inputColor === selectedColorNormalized) {
                input.checked = true;
              } else {
                input.checked = false;
              }
            }
          });

          // Limit visible swatches to 4 and add "Show more" dropdown
          const swatchesContainer = colorSwatchesContainer.querySelector('.custom-color-swatches__container');
          let dropdown = null; // Declare dropdown in outer scope
          let updateAllSwatchSelection = null; // Declare function in outer scope

          if (swatchesContainer) {
            const allSwatches = Array.from(swatchesContainer.querySelectorAll('.custom-color-swatch'));
            const maxVisible = 4;

            // Create updateAllSwatchSelection function using helper method
            // Note: dropdown will be created later, so we'll update this function after dropdown is created
            updateAllSwatchSelection = this.createUpdateAllSwatchSelectionFunction(swatchesContainer, null);

            if (allSwatches.length > maxVisible) {
              // Hide swatches beyond the first 4
              allSwatches.forEach((swatch, index) => {
                if (index >= maxVisible) {
                  swatch.classList.add('color-swatch-hidden');
                  swatch.style.display = 'none';
                }
              });

              /* Wrapper for visible 4 swatches + dropdown so dropdown opens under them at same width */
              const visibleRowWrapper = document.createElement('div');
              visibleRowWrapper.className = 'kit-builder-color-swatches-visible-row';

              const showMoreContainer = document.createElement('div');
              showMoreContainer.className = 'color-swatches-show-more-container';

              const showMoreButton = document.createElement('button');
              showMoreButton.type = 'button';
              showMoreButton.className = 'color-swatches-show-more-btn';
              showMoreButton.textContent = 'Show more';
              showMoreButton.setAttribute('aria-expanded', 'false');
              showMoreButton.setAttribute('aria-label', 'Show more color options');

              dropdown = document.createElement('div');
              dropdown.className = 'color-swatches-dropdown';
              dropdown.style.display = 'none';

              updateAllSwatchSelection = this.createUpdateAllSwatchSelectionFunction(swatchesContainer, dropdown);

              /* Move first 4 swatches into wrapper so dropdown can sit under them */
              allSwatches.slice(0, maxVisible).forEach((sw) => visibleRowWrapper.appendChild(sw));

              const hiddenSwatches = allSwatches.slice(maxVisible);
              hiddenSwatches.forEach((swatch) => {
                const dropdownRow = document.createElement('div');
                dropdownRow.className = 'color-swatches-dropdown-row';

                const clonedSwatch = swatch.cloneNode(true);
                clonedSwatch.style.display = 'inline-block';
                clonedSwatch.classList.remove('color-swatch-hidden', 'custom-color-swatch--selected');
                const clonedLabel = clonedSwatch.querySelector('.custom-color-swatch__label');
                if (clonedLabel) {
                  clonedLabel.classList.remove('custom-color-swatch--selected');
                }
                const clonedInput = clonedSwatch.querySelector('input[type="radio"]');
                if (clonedInput) {
                  clonedInput.checked = false;
                  clonedInput.removeAttribute('checked');
                }

                const colorName = (swatch.getAttribute('title') || swatch.dataset.value || swatch.querySelector('input[type="radio"]')?.value || '').toString().trim();
                const clonedTooltip = clonedSwatch.querySelector('.custom-color-swatch__tooltip');
                if (clonedTooltip) clonedTooltip.remove();
                clonedSwatch.removeAttribute('data-tooltip');

                const colorNameSpan = document.createElement('span');
                colorNameSpan.className = 'color-swatches-dropdown-name';
                colorNameSpan.textContent = colorName;

                dropdownRow.appendChild(clonedSwatch);
                dropdownRow.appendChild(colorNameSpan);

                this.handleDropdownRowClick({
                  dropdownRow,
                  clonedSwatch,
                  swatch,
                  swatchesContainer,
                  dropdown,
                  showMoreButton,
                  productHandle: bd.handle,
                  updateAllSwatchSelection,
                });

                dropdown.appendChild(dropdownRow);
              });

              showMoreContainer.appendChild(showMoreButton);
              showMoreContainer.appendChild(dropdown);
              swatchesContainer.insertBefore(visibleRowWrapper, swatchesContainer.firstChild);
              swatchesContainer.appendChild(showMoreContainer);

              showMoreButton.addEventListener('click', (e) => {
                e.stopPropagation();
                const isExpanded = showMoreButton.getAttribute('aria-expanded') === 'true';
                if (isExpanded) {
                  this.closeColorDropdown(dropdown, showMoreButton);
                } else {
                  dropdown.style.display = 'block';
                  showMoreButton.setAttribute('aria-expanded', 'true');
                  showMoreButton.textContent = 'Show less';
                }
              });

              document.addEventListener('click', (e) => {
                if (!showMoreContainer.contains(e.target)) {
                  this.closeColorDropdown(dropdown, showMoreButton);
                }
              });
            }

            // Add click handlers - only add tooltips to first 4 swatches (dropdown shows names for rest)
            const allSwatchesList = Array.from(swatchesContainer.querySelectorAll('.custom-color-swatch'));
            const visibleSwatchesOnly = allSwatchesList.slice(0, maxVisible);
            visibleSwatchesOnly.forEach((swatch) => {
              const input = swatch.querySelector('input[type="radio"]');
              const label = swatch.querySelector('label.custom-color-swatch__label');

              if (!input || input.disabled) return;

              // Tooltip only for first 4 - sanitize to remove weird chars (??, \, |, etc)
              let tooltipText = (input.value || swatch.dataset.value || swatch.getAttribute('title') || '').toString().trim();
              if (tooltipText) {
                tooltipText = tooltipText.replace(/[^\w\s-]/g, '').replaceAll(/\s+/g, ' ').trim();
                tooltipText = tooltipText.toUpperCase();
                if (tooltipText) {
                  swatch.querySelectorAll('.custom-color-swatch__tooltip').forEach((el) => el.remove());
                  const tooltipElement = document.createElement('div');
                  tooltipElement.className = 'custom-color-swatch__tooltip';
                  tooltipElement.textContent = tooltipText;
                  swatch.appendChild(tooltipElement);
                  swatch.dataset.tooltip = tooltipText;
                }
                swatch.removeAttribute('title');
                if (label) {
                  label.removeAttribute('title');
                  label.style.pointerEvents = 'auto';
                }
              }

              // Handler function to update selection using helper method
              const handleColorSelection = (e) => {
                this.handleSwatchColorSelection(e, {
                  input,
                  swatch,
                  swatchesContainer,
                  productHandle: bd.handle,
                  updateColorLabels,
                });
              };

              // Attach handler to both the swatch container and the label
              swatch.addEventListener('click', handleColorSelection, true); // Use capture phase
              if (label) {
                label.addEventListener('click', handleColorSelection, true); // Also on label
              }
            });

            // Listen for selection changes on all inputs - scoped to this product only
            // IMPORTANT: This must be outside the dropdown conditional so it works for all products
            this.setupSwatchChangeListener(
              swatchesContainer,
              bd.handle,
              updateAllSwatchSelection,
              updateColorLabels
            );

            // Also listen for custom color swatch events from the snippet's JavaScript
            const swatchesElement = colorSwatchesContainer.querySelector('.custom-color-swatches');
            if (swatchesElement) {
              swatchesElement.addEventListener('custom-color-swatch:changed', (e) => {
                const detail = e.detail;
                if (detail?.value) {
                  const normalizedHandle = this.normalizeHandle(bd.handle);
                  this.selectedColors[normalizedHandle] = detail.value;

                  // Update color labels
                  updateColorLabels(detail.value);

                  // Optimize: Only update variants for this specific product
                  this.updateProductVariants(bd.handle);

                  this.updateProductImageForColor(bd.handle, detail.value);
                }
              });
            }

            // Initial update
            updateAllSwatchSelection();
          }
        }

        colorSwatchesWrapper.appendChild(colorLabelAbove);
        colorSwatchesWrapper.appendChild(colorSwatchesContainer);

        if (this.showRecommendedColorForHandle(bd.data.handle)) {
          const recommendedColorLabel = document.createElement('div');
          recommendedColorLabel.className = 'product-recommended-color';
          recommendedColorLabel.style.fontSize = '0.875rem';
          recommendedColorLabel.style.color = '#6b7280';
          recommendedColorLabel.style.marginTop = '0.5rem';
          recommendedColorLabel.textContent = 'Recommended color: White';
          colorSwatchesWrapper.appendChild(recommendedColorLabel);
        }

        colorSwatchesAndToggleContainer.appendChild(colorSwatchesWrapper);

        // Add full cover toggle for waterproof-sealant product (to the right of color swatches)
        if (isWaterproofSealant && this.showFullCoverToggle) {
          const fullCoverToggleContainer = document.createElement('div');
          fullCoverToggleContainer.className = 'product-full-cover-toggle-container';
          fullCoverToggleContainer.style.display = 'flex';
          fullCoverToggleContainer.style.flexDirection = 'column';
          fullCoverToggleContainer.style.alignItems = 'flex-start';
          fullCoverToggleContainer.style.gap = '0.5rem';

          const toggleLabelWrapper = document.createElement('div');
          toggleLabelWrapper.className = 'product-full-cover-toggle-label-wrapper';
          toggleLabelWrapper.style.display = 'flex';
          toggleLabelWrapper.style.alignItems = 'center';
          toggleLabelWrapper.style.gap = '0.5rem';

          const toggleLabel = document.createElement('span');
          toggleLabel.className = 'product-full-cover-toggle-label';
          toggleLabel.style.fontSize = '1rem';
          toggleLabel.style.fontWeight = '500';
          toggleLabel.style.color = '#000000';
          toggleLabel.textContent = this.fullCoverToggleLabel;

          toggleLabelWrapper.appendChild(toggleLabel);

          if (this.fullCoverTooltipText) {
            const tooltipContainer = document.createElement('div');
            tooltipContainer.className = 'product-full-cover-toggle-tooltip-container';
            tooltipContainer.style.position = 'relative';
            tooltipContainer.style.display = 'inline-flex';

            const tooltipTrigger = document.createElement('button');
            tooltipTrigger.type = 'button';
            tooltipTrigger.className = 'product-full-cover-toggle-tooltip-trigger';
            tooltipTrigger.setAttribute('aria-label', 'Toggle information');
            tooltipTrigger.style.display = 'inline-flex';
            tooltipTrigger.style.alignItems = 'center';
            tooltipTrigger.style.justifyContent = 'center';
            tooltipTrigger.style.width = '1.125rem';
            tooltipTrigger.style.height = '1.125rem';
            tooltipTrigger.style.padding = '0';
            tooltipTrigger.style.backgroundColor = 'transparent';
            tooltipTrigger.style.border = '1px solid #16a34a';
            tooltipTrigger.style.borderRadius = '50%';
            tooltipTrigger.style.color = '#16a34a';
            tooltipTrigger.style.fontSize = '0.75rem';
            tooltipTrigger.style.fontWeight = '700';
            tooltipTrigger.style.lineHeight = '1';
            tooltipTrigger.style.cursor = 'pointer';
            tooltipTrigger.textContent = 'i';

            const tooltip = document.createElement('div');
            tooltip.className = 'product-full-cover-toggle-tooltip';
            tooltip.textContent = this.fullCoverTooltipText;

            tooltipTrigger.addEventListener('click', (e) => {
              e.stopPropagation();
              const isActive = tooltipContainer.classList.contains('active');

              // Close all other tooltips first
              const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
              const allTooltipContainers = document.querySelectorAll(
                `${sectionPrefix} .product-why-tooltip-container, ${sectionPrefix} .product-full-cover-toggle-tooltip-container`
              );
              allTooltipContainers.forEach((container) => {
                if (container !== tooltipContainer) {
                  container.classList.remove('active');
                }
              });

              // Toggle this tooltip
              if (isActive) {
                tooltipContainer.classList.remove('active');
              } else {
                tooltipContainer.classList.add('active');
              }
            });

            tooltipContainer.appendChild(tooltipTrigger);
            tooltipContainer.appendChild(tooltip);
            toggleLabelWrapper.appendChild(tooltipContainer);
          }

          const toggleWrapper = document.createElement('label');
          toggleWrapper.className = 'roof-type-toggle';
          toggleWrapper.style.position = 'relative';
          toggleWrapper.style.display = 'inline-block';
          toggleWrapper.style.width = '4rem';
          toggleWrapper.style.height = '2rem';
          toggleWrapper.style.cursor = 'pointer';
          toggleWrapper.setAttribute('for', `full-cover-toggle-${this.sectionIdOnly}`);

          const toggleInput = document.createElement('input');
          toggleInput.type = 'checkbox';
          toggleInput.id = `full-cover-toggle-${this.sectionIdOnly}`;
          toggleInput.className = 'roof-type-toggle-input';
          toggleInput.setAttribute('aria-label', 'Toggle full cover');
          toggleInput.dataset.fullCover = 'false';
          toggleInput.dataset.calculatorId = this.sectionIdOnly;
          toggleInput.checked = this.fullCover;

          // Set up event listener for the toggle
          toggleInput.addEventListener('change', () => this.handleFullCoverToggle());

          const toggleSlider = document.createElement('span');
          toggleSlider.className = 'roof-type-toggle-slider';

          toggleWrapper.appendChild(toggleInput);
          toggleWrapper.appendChild(toggleSlider);

          fullCoverToggleContainer.appendChild(toggleLabelWrapper);
          fullCoverToggleContainer.appendChild(toggleWrapper);

          colorSwatchesAndToggleContainer.appendChild(fullCoverToggleContainer);
        }

        titleColumn.appendChild(colorSwatchesAndToggleContainer);
      }

      cardContent.appendChild(titleColumn);

      // Right column: Variants only
      const variantsColumn = document.createElement('div');
      variantsColumn.className = 'product-card-variants-column';

      const variantsDiv = document.createElement('div');
      variantsDiv.className = 'product-card-variants';

      // If no buckets, show available variants from product data (but not for rapid-cure-spray)
      if (
        bd.buckets.length === 0 &&
        bd.data.variants &&
        bd.data.variants.length > 0 &&
        bd.handle !== 'rapid-cure-spray'
      ) {
        bd.data.variants.forEach((variant) => {
          const variantItem = document.createElement('div');
          variantItem.className = 'variant-item';

          const img = document.createElement('img');
          img.src = variant.imageUrl || bd.data.featured_image || '/placeholder.png';
          img.alt = variant.label || '';
          img.className = 'variant-image';

          const variantInfo = document.createElement('div');
          variantInfo.className = 'variant-info';

          const label = document.createElement('div');
          label.className = 'variant-label';
          label.textContent = variant.label || `${variant.size || ''}L`;

          const price = document.createElement('div');
          price.className = 'variant-price';
          price.textContent = `AUD ${variant.price.toFixed(2)}`;

          variantInfo.appendChild(label);
          variantInfo.appendChild(price);

          const countDiv = document.createElement('div');
          countDiv.className = 'variant-count';

          const decreaseBtn = document.createElement('button');
          decreaseBtn.className = 'variant-count-btn';
          decreaseBtn.textContent = '-';
          decreaseBtn.disabled = true;

          const countInput = document.createElement('input');
          countInput.type = 'number';
          countInput.className = 'variant-count-input';
          countInput.value = 0;
          countInput.min = 0;
          countInput.disabled = true;

          const increaseBtn = document.createElement('button');
          increaseBtn.className = 'variant-count-btn';
          increaseBtn.textContent = '+';
          increaseBtn.disabled = true;

          countDiv.appendChild(decreaseBtn);
          countDiv.appendChild(countInput);
          countDiv.appendChild(increaseBtn);

          variantItem.appendChild(img);
          variantItem.appendChild(variantInfo);
          variantItem.appendChild(countDiv);
          variantsDiv.appendChild(variantItem);
        });
      }

      bd.buckets.forEach((bucket) => {
        const variantItem = document.createElement('div');
        variantItem.className = 'variant-item';

        const img = document.createElement('img');
        img.src = bucket.imageUrl || '/placeholder.png';
        img.alt = bucket.label || '';
        img.className = 'variant-image';

        const variantInfo = document.createElement('div');
        variantInfo.className = 'variant-info';

        const label = document.createElement('div');
        label.className = 'variant-label';
        label.textContent = bucket.label || `${this.formatDecimal(bucket.size)}L`;

        const price = document.createElement('div');
        price.className = 'variant-price';
        price.textContent = `AUD ${bucket.price.toFixed(2)}`;

        variantInfo.appendChild(label);
        variantInfo.appendChild(price);

        const countDiv = document.createElement('div');
        countDiv.className = 'variant-count';

        const countInput = document.createElement('input');
        countInput.type = 'number';
        countInput.className = 'variant-count-input';
        countInput.value = bucket.count;
        countInput.min = 0;

        const decreaseBtn = document.createElement('button');
        decreaseBtn.className = 'variant-count-btn';
        decreaseBtn.textContent = '-';
        decreaseBtn.addEventListener('click', () => {
          if (bucket.count > 0) {
            bucket.count--;
            countInput.value = bucket.count;
            this.updateCalculations();
          }
        });

        countInput.addEventListener('change', (e) => {
          bucket.count = Math.max(0, Number.parseInt(e.target.value, 10) || 0);
          countInput.value = bucket.count;
          this.updateCalculations();
        });

        const increaseBtn = document.createElement('button');
        increaseBtn.className = 'variant-count-btn';
        increaseBtn.textContent = '+';
        increaseBtn.addEventListener('click', () => {
          bucket.count++;
          countInput.value = bucket.count;
          this.updateCalculations();
        });

        countDiv.appendChild(decreaseBtn);
        countDiv.appendChild(countInput);
        countDiv.appendChild(increaseBtn);

        variantItem.appendChild(img);
        variantItem.appendChild(variantInfo);
        variantItem.appendChild(countDiv);
        variantsDiv.appendChild(variantItem);
      });

      // Color picker if available
      const colors = bd.data.colors || [];
      if (colors.length > 0) {
        const colorPicker = document.createElement('div');
        colorPicker.className = 'color-picker';

        colors.forEach((color) => {
          const colorOption = document.createElement('div');
          colorOption.className = 'color-option';
          colorOption.style.backgroundColor = this.getColorHex(color);
          if (this.selectedColors[bd.handle] === color) {
            colorOption.classList.add('selected');
          }
          colorOption.addEventListener('click', () => {
            this.selectedColors[bd.handle] = color;
            this.updateCalculations();
          });
          colorPicker.appendChild(colorOption);
        });

        variantsDiv.appendChild(colorPicker);
      }

      variantsColumn.appendChild(variantsDiv);
      cardContent.appendChild(variantsColumn);
      card.appendChild(cardContent);

      container.appendChild(card);
    });
  }

  getColorHex(colorName) {
    // Basic color mapping - you may need to expand this
    const colorMap = {
      White: '#ffffff',
      Black: '#000000',
      Red: '#ff0000',
      Blue: '#0000ff',
      Green: '#008000',
      Grey: '#808080',
      Gray: '#808080',
    };
    return colorMap[colorName] || '#cccccc';
  }

  updatePricing() {
    const totalPrice = this.breakdowns
      .filter((b) => b.selected && this.isVisible(b.handle))
      .flatMap((b) => b.buckets)
      .reduce((s, b) => s + b.count * b.price, 0);

    this.originalPrice = totalPrice;
    // If discount rate is 0, no discount is applied
    if (this.discountRate > 0) {
      this.discountedPrice = Math.round(totalPrice * (1 - this.discountRate) * 100) / 100;
      this.savings = Math.round((totalPrice - this.discountedPrice) * 100) / 100;
    } else {
      // No discount - use original price
      this.discountedPrice = totalPrice;
      this.savings = 0;
    }

    // Calculate total items from visible selected products
    const visibleItems = this.breakdowns
      .filter((b) => b.selected && this.isVisible(b.handle))
      .reduce((sum, b) => sum + b.buckets.reduce((s, bk) => s + bk.count, 0), 0);

    // Add bonus kit count (always 1 item if area > 0, since bonus kit is always included)
    const area = this.getArea();
    const bonusKitCount = area > 0 ? 1 : 0;

    this.totalItems = visibleItems + bonusKitCount;

    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');

    // Update UI elements
    const totalItemsEl = document.querySelector(`${sectionPrefix} #total-items-${sectionIdOnly}`);
    if (totalItemsEl) totalItemsEl.textContent = this.totalItems;

    const savingsEl = document.querySelector(`${sectionPrefix} #savings-${sectionIdOnly}`);
    if (savingsEl) savingsEl.textContent = this.savings.toFixed(2);

    const discountPercentageEl = document.querySelector(`${sectionPrefix} #discount-percentage-${sectionIdOnly}`);
    if (discountPercentageEl && this.discountRate > 0) {
      const discountPercent = Math.round(this.discountRate * 100);
      discountPercentageEl.textContent = `${discountPercent}% OFF`;
    }

    const originalPriceMobile = document.querySelector(`${sectionPrefix} #original-price-mobile-${sectionIdOnly}`);
    if (originalPriceMobile) originalPriceMobile.textContent = this.originalPrice.toFixed(2);

    const discountedPriceMobile = document.querySelector(`${sectionPrefix} #discounted-price-mobile-${sectionIdOnly}`);
    if (discountedPriceMobile) discountedPriceMobile.textContent = this.discountedPrice.toFixed(2);

    const originalPriceDesktop = document.querySelector(`${sectionPrefix} #original-price-desktop-${sectionIdOnly}`);
    if (originalPriceDesktop) originalPriceDesktop.textContent = this.originalPrice.toFixed(2);

    const discountedPriceDesktop = document.querySelector(
      `${sectionPrefix} #discounted-price-desktop-${sectionIdOnly}`
    );
    if (discountedPriceDesktop) discountedPriceDesktop.textContent = this.discountedPrice.toFixed(2);

    // Check if any product has quantity > 0 (excluding bonus kit)
    // visibleItems already excludes brush-roller and only counts products with count > 0
    const hasProductsWithQuantity = visibleItems > 0;

    const ctaBtn = document.querySelector(`${sectionPrefix} #cta-kit-builder-${sectionIdOnly}`);
    if (ctaBtn) {
      ctaBtn.disabled = !hasProductsWithQuantity;
    }
  }

  /**
   * Build variant URL for fetching from Shopify API
   * @param {string|number} variantId - The variant ID
   * @returns {string} The variant URL
   */
  buildVariantUrl(variantId) {
    const root = globalThis.Shopify?.routes?.root || '/';
    let variantUrl = `${root}variants/${variantId}.js`.replace('//', '/');
    // If root is just '/', make sure we have the correct format
    if (variantUrl === `/variants/${variantId}.js`) {
      variantUrl = `/variants/${variantId}.js`;
    }
    return variantUrl;
  }

  /**
   * Extract image URL from variant object
   * @param {Object} variant - The variant object from Shopify API
   * @returns {string|null} The image URL or null
   */
  extractImageFromVariant(variant) {
    // Try variant-specific image fields first
    if (variant.featured_image) {
      return variant.featured_image;
    }
    if (variant.image) {
      return variant.image;
    }
    if (variant.featured_media?.preview?.image?.url) {
      return variant.featured_media.preview.image.url;
    }
    if (variant.media && variant.media.length > 0) {
      const firstMedia = variant.media[0];
      if (firstMedia.preview?.image?.url) {
        return firstMedia.preview.image.url;
      }
      if (firstMedia.src) {
        return firstMedia.src;
      }
    }
    return null;
  }

  /**
   * Fetch product image as fallback when variant has no image
   * @param {string|number} productId - The product ID
   * @param {string} root - The Shopify routes root
   * @returns {Promise<string|null>} The image URL or null
   */
  async fetchProductImage(productId, root) {
    try {
      const productUrl = `${root}products/${productId}.js`.replace('//', '/');
      const productResponse = await fetch(productUrl);
      if (!productResponse.ok) {
        return null;
      }
      const product = await productResponse.json();
      const imageUrl =
        product.featured_image || product.featured_media?.preview?.image?.url || product.images?.[0] || null;
      return imageUrl;
    } catch {
      return null;
    }
  }

  /**
   * Normalize image URL to ensure proper format
   * @param {string|Object|null} imageUrl - The image URL (may be string, object, or null)
   * @returns {string|null} Normalized image URL or null
   */
  normalizeImageUrl(imageUrl) {
    if (!imageUrl) {
      return null;
    }

    // Handle case where imageUrl might be an object
    if (typeof imageUrl === 'object' && imageUrl !== null) {
      imageUrl = imageUrl.src || imageUrl.url || imageUrl.href || null;
    }

    // Only proceed if we have a valid string value
    if (!imageUrl || typeof imageUrl !== 'string') {
      return null;
    }

    // Normalize URL format
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl; // Already a full URL
    }
    if (imageUrl.startsWith('//')) {
      return `https:${imageUrl}`; // Protocol-relative URL
    }
    if (!imageUrl.startsWith('/')) {
      return `/${imageUrl}`; // Relative URL without leading slash
    }
    return imageUrl; // Already a proper path
  }

  /**
   * Build the final variant response object
   * @param {Object} variant - The variant object from Shopify API
   * @param {string|null} imageUrl - The normalized image URL
   * @returns {Object} The formatted variant response
   */
  buildVariantResponse(variant, imageUrl) {
    return {
      id: variant.id,
      product_id: variant.product_id,
      title: variant.title,
      price: variant.price / 100,
      image: imageUrl,
      available: variant.available,
      product_title: variant.product_title || '',
      product_handle: variant.product_handle || '',
    };
  }

  // Helper method to fetch variant information from Shopify
  async fetchVariantInfo(variantId) {
    if (!variantId) return null;

    try {
      const variantUrl = this.buildVariantUrl(variantId);
      const response = await fetch(variantUrl);
      if (!response.ok) {
        return null;
      }

      const variant = await response.json();
      let imageUrl = this.extractImageFromVariant(variant);

      // If still no image, try to get from product if available
      if (!imageUrl && variant.product_id) {
        const root = globalThis.Shopify?.routes?.root || '/';
        imageUrl = await this.fetchProductImage(variant.product_id, root);
      }

      // Normalize image URL
      imageUrl = this.normalizeImageUrl(imageUrl);

      return this.buildVariantResponse(variant, imageUrl);
    } catch {
      return null;
    }
  }

  /**
   * Determine which bonus product to use based on area threshold
   * @param {number} area - The calculated area
   * @returns {Object} Object with useBrush, handle, and variantId
   */
  determineBonusProduct(area) {
    const useBrush = area < this.bonusKitAreaThreshold;
    return {
      useBrush,
      handle: useBrush ? this.bonusBrushProductHandle : this.bonusBrushRollerProductHandle,
      variantId: useBrush ? this.bonusBrushVariantId : this.bonusBrushRollerVariantId,
    };
  }

  /**
   * Check if there are any products in the kit (excluding bonus kit products)
   * @param {string} bonusBrushHandle - Brush product handle
   * @param {string} bonusBrushRollerHandle - Brush roller product handle
   * @returns {boolean} True if there are products in the kit
   */
  hasProductsInKit(bonusBrushHandle, bonusBrushRollerHandle) {
    return this.breakdowns.some(
      (bd) =>
        bd.handle !== 'brush-roller' &&
        (!bonusBrushHandle || bd.handle !== bonusBrushHandle) &&
        (!bonusBrushRollerHandle || bd.handle !== bonusBrushRollerHandle) &&
        bd.selected &&
        this.isVisible(bd.handle) &&
        bd.buckets?.some((b) => b.count > 0)
    );
  }

  /**
   * Determine if bonus banner should be shown
   * @param {number} area - The calculated area
   * @param {boolean} hasProductsInKit - Whether there are products in the kit
   * @param {boolean} hasBonusConfig - Whether bonus configuration exists
   * @returns {boolean} True if banner should be shown
   */
  shouldShowBonusBanner(area, hasProductsInKit, hasBonusConfig) {
    return area > 0 && hasProductsInKit && hasBonusConfig;
  }

  /**
   * Find image for a product by handle
   * @param {string} handle - Product handle
   * @param {string} fallback - Fallback image path
   * @returns {string} Image URL or fallback
   */
  findImageForProduct(handle, fallback) {
    const product = this.products.find((p) => p.handle === handle);
    if (!product) return fallback;
    const variantWithImage = product.variants?.find((v) => v.imageUrl) || {};
    return variantWithImage.imageUrl || product.featured_image || fallback;
  }

  /**
   * Find bonus kit image from product handle
   * @param {boolean} useBrush - Whether to use brush product
   * @param {string} currentBonusHandle - Current bonus product handle
   * @param {string} currentBonusVariantId - Current bonus variant ID
   * @returns {string|null} Image URL or null
   */
  findBonusKitImageFromProduct(useBrush, currentBonusHandle, currentBonusVariantId) {
    const fallbackImage = useBrush
      ? '/images/products/liquid-rubber-diy-brush-set-caravan-roof.webp'
      : '/images/products/liquid-rubber-diy-brush-roller-set-caravan-roof.webp';
    let bonusKitImage = this.findImageForProduct(currentBonusHandle, fallbackImage);

    // If we have a specific variant ID, try to get its image
    if (currentBonusVariantId) {
      const product = this.products.find((p) => p.handle === currentBonusHandle);
      if (product) {
        const variant = product.variants?.find((v) => String(v.variantId) === String(currentBonusVariantId));
        if (variant?.imageUrl) {
          bonusKitImage = variant.imageUrl;
        }
      }
    }

    return bonusKitImage;
  }

  /**
   * Find bonus kit image from variant ID
   * @param {string} currentBonusVariantId - Current bonus variant ID
   * @returns {Promise<string|null>} Image URL or null
   */
  async findBonusKitImageFromVariantId(currentBonusVariantId) {
    const variantInfo = await this.fetchVariantInfo(currentBonusVariantId);
    if (!variantInfo) {
      return null;
    }
    return variantInfo.image || null;
  }

  /**
   * Find bonus kit image using handle or variant ID
   * @param {boolean} useBrush - Whether to use brush product
   * @param {string} currentBonusHandle - Current bonus product handle
   * @param {string} currentBonusVariantId - Current bonus variant ID
   * @returns {Promise<string|null>} Image URL or null
   */
  async findBonusKitImage(useBrush, currentBonusHandle, currentBonusVariantId) {
    if (currentBonusHandle) {
      return this.findBonusKitImageFromProduct(useBrush, currentBonusHandle, currentBonusVariantId);
    }

    if (currentBonusVariantId) {
      return await this.findBonusKitImageFromVariantId(currentBonusVariantId);
    }

    return null;
  }

  /**
   * Create error handler for image element
   * @param {HTMLElement} imageEl - Image element
   * @param {string} fallbackImage - Fallback image path
   * @returns {Function} Error handler function
   */
  createImageErrorHandler(imageEl, fallbackImage) {
    let errorHandlerCalled = false;
    return function () {
      if (errorHandlerCalled) return;
      errorHandlerCalled = true;

      const currentSrcPath = new URL(this.src, globalThis.location.origin).pathname;
      const fallbackPath = fallbackImage;
      if (currentSrcPath !== fallbackPath) {
        this.src = fallbackImage;
      }
      this.onerror = null;
    };
  }

  /**
   * Update bonus kit image element
   * @param {HTMLElement} imageEl - Image element to update
   * @param {string|null} bonusKitImage - Image URL or null
   * @param {string} bonusKitText - Alt text for image
   * @param {boolean} useBrush - Whether to use brush product
   */
  updateBonusKitImage(imageEl, bonusKitImage, bonusKitText, useBrush) {
    imageEl.onerror = null;

    if (!bonusKitImage) {
      imageEl.style.display = 'none';
      imageEl.alt = bonusKitText;
      return;
    }

    const imageUrl = this.normalizeImageUrl(bonusKitImage);
    const currentSrc = imageEl.src.split('?')[0];
    const newSrc = imageUrl.split('?')[0];

    if (currentSrc !== newSrc && !currentSrc.endsWith(newSrc) && !newSrc.endsWith(currentSrc)) {
      imageEl.src = imageUrl;
      imageEl.alt = bonusKitText;

      const fallbackImage = useBrush
        ? '/images/products/liquid-rubber-diy-brush-set-caravan-roof.webp'
        : '/images/products/liquid-rubber-diy-brush-roller-set-caravan-roof.webp';
      imageEl.onerror = this.createImageErrorHandler(imageEl, fallbackImage);
    } else {
      imageEl.alt = bonusKitText;
    }
  }

  /**
   * Update all bonus kit UI elements
   * @param {string} sectionPrefix - Section prefix selector
   * @param {string} sectionIdOnly - Section ID only
   * @param {string} bonusKitValue - Bonus kit value text
   * @param {string} bonusKitText - Bonus kit text
   * @param {string|null} bonusKitImage - Bonus kit image URL
   * @param {boolean} useBrush - Whether to use brush product
   * @param {boolean} shouldShowBonus - Whether to show bonus banner
   */
  updateBonusKitUI(
    sectionPrefix,
    sectionIdOnly,
    bonusKitValue,
    bonusKitText,
    bonusKitImage,
    useBrush,
    shouldShowBonus
  ) {
    const valueEl = document.querySelector(`${sectionPrefix} #bonus-kit-value-${sectionIdOnly}`);
    if (valueEl) valueEl.textContent = bonusKitValue;

    const textEl = document.querySelector(`${sectionPrefix} #bonus-kit-text-${sectionIdOnly}`);
    if (textEl) textEl.textContent = bonusKitText;

    const imageEl = document.querySelector(`${sectionPrefix} #bonus-kit-image-${sectionIdOnly}`);
    if (imageEl) {
      this.updateBonusKitImage(imageEl, bonusKitImage, bonusKitText, useBrush);
    }

    const bannerEl = document.querySelector(`${sectionPrefix} #bonus-kit-banner-${sectionIdOnly}`);
    if (bannerEl) {
      bannerEl.style.display = shouldShowBonus ? '' : 'none';
    }
  }

  async updateBonusKit(area) {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');

    const bonusProduct = this.determineBonusProduct(area);
    const { useBrush, handle: currentBonusHandle, variantId: currentBonusVariantId } = bonusProduct;

    const hasBonusConfig = currentBonusHandle || currentBonusVariantId;
    const hasProductsInKit = this.hasProductsInKit(this.bonusBrushProductHandle, this.bonusBrushRollerProductHandle);
    const shouldShowBonus = this.shouldShowBonusBanner(area, hasProductsInKit, hasBonusConfig);

    const bonusKitValue = useBrush ? this.bonusBrushPrice : this.bonusBrushRollerPrice;
    const bonusKitText = useBrush ? this.bonusBrushText : this.bonusBrushRollerText;

    const bonusKitImage = await this.findBonusKitImage(useBrush, currentBonusHandle, currentBonusVariantId);

    this.updateBonusKitUI(
      sectionPrefix,
      sectionIdOnly,
      bonusKitValue,
      bonusKitText,
      bonusKitImage,
      useBrush,
      shouldShowBonus
    );
  }

  buildCartParam() {
    return this.breakdowns
      .filter((b) => b.selected && (this.isVisible(b.handle) || b.handle === 'brush-roller'))
      .flatMap((b) =>
        b.buckets
          .filter((bk) => bk.count > 0 && bk.variantId)
          .map((bk) => {
            // variantId might be a number or string, handle both cases
            const variantId = typeof bk.variantId === 'string' ? bk.variantId.split('/').pop() : String(bk.variantId);
            return `${variantId}:${bk.count}`;
          })
      )
      .join(',');
  }

  /**
   * Generates the cart payload (variant IDs and quantities) from the
   * calculated breakdowns, including the bonus product.
   * @returns {Array<{id: string|number, quantity: number}>}
   */
  /**
   * Validate that there are items to add to cart
   */
  validateCartItems() {
    const visibleItems = this.breakdowns
      .filter((b) => b.selected && this.isVisible(b.handle))
      .reduce((sum, b) => sum + b.buckets.reduce((s, bk) => s + bk.count, 0), 0);

    if (visibleItems === 0) {
      this.showPopup('Please add at least 1 product to your kit');
      return false;
    }
    return true;
  }

  /**
   * Get cart elements (drawer or notification)
   */
  getCartElements() {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartNotification = document.querySelector('cart-notification');
    return { cartDrawer, cartNotification };
  }

  /**
   * Get CTA button element
   */
  getCtaButton() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    return document.querySelector(`${sectionPrefix} #cta-kit-builder-${sectionIdOnly}`);
  }

  /**
   * Set button loading state
   */
  setButtonLoading(ctaBtn, isLoading) {
    if (!ctaBtn) return;
    const spinner = ctaBtn.querySelector('.loading__spinner');
    ctaBtn.disabled = isLoading;
    if (isLoading) {
      ctaBtn.classList.add('loading');
      if (spinner) {
        spinner.classList.remove('hidden');
      }
    } else {
      ctaBtn.classList.remove('loading');
      if (spinner) {
        spinner.classList.add('hidden');
      }
    }
  }

  /**
   * Build FormData for adding an item to cart
   */
  buildCartItemFormData(item, isLastItem, cartElement) {
    const formData = new FormData();
    formData.append('id', item.id);
    formData.append('quantity', item.quantity);

    if (isLastItem && cartElement) {
      formData.append(
        'sections',
        cartElement
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',')
      );
      formData.append('sections_url', globalThis.location.pathname);
    }

    return formData;
  }

  /**
   * Add a single item to cart
   */
  async addItemToCart(item, isLastItem, cartElement) {
    const config = typeof fetchConfig === 'undefined' ? this.getFetchConfig() : fetchConfig('javascript');
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = this.buildCartItemFormData(item, isLastItem, cartElement);
    config.body = formData;

    const cartAddUrl = routes?.cart_add_url || globalThis.routes?.cart_add_url || '/cart/add.js';
    const response = await fetch(cartAddUrl, config);

    if (!response.ok) {
      throw new Error('Error adding item to cart. Please try again.');
    }

    const addResponse = await response.json();

    if (addResponse.status) {
      if (globalThis.PUB_SUB_EVENTS && typeof globalThis.publish === 'function') {
        globalThis.publish(globalThis.PUB_SUB_EVENTS.cartError, {
          source: 'kit-builder',
          productVariantId: item.id,
          errors: addResponse.errors || addResponse.description,
          message: addResponse.message,
        });
      }
      throw new Error(addResponse.description || 'Error adding item to cart');
    }

    return addResponse;
  }

  /**
   * Add all items to cart sequentially
   */
  async addAllItemsToCart(itemsToAdd, cartElement) {
    let lastAddResponse = null;

    for (let i = 0; i < itemsToAdd.length; i++) {
      const item = itemsToAdd[i];
      const isLastItem = i === itemsToAdd.length - 1;
      const addResponse = await this.addItemToCart(item, isLastItem, cartElement);

      if (isLastItem) {
        lastAddResponse = addResponse;
      }
    }

    if (!lastAddResponse?.sections) {
      throw new Error('Error loading cart. Please try again.');
    }

    return lastAddResponse;
  }

  /**
   * Verify cart drawer response contains items
   */
  verifyCartDrawerResponse(lastAddResponse) {
    if (!lastAddResponse.sections['cart-drawer']) {
      return false;
    }

    const verifyDoc = new DOMParser().parseFromString(lastAddResponse.sections['cart-drawer'], 'text/html');
    const verifyItems = verifyDoc.querySelectorAll('.cart-item, [id^="CartDrawer-Item"]');

    if (verifyItems.length === 0) {
      return false;
    }

    return true;
  }

  /**
   * Publish cart update event
   */
  async publishCartUpdateEvent(itemsToAdd, lastAddResponse) {
    if (!globalThis.PUB_SUB_EVENTS || typeof globalThis.publish !== 'function') {
      return;
    }

    const startMarker = globalThis.CartPerformance?.createStartingMarker?.('add:wait-for-subscribers');
    await globalThis.publish(globalThis.PUB_SUB_EVENTS.cartUpdate, {
      source: 'kit-builder',
      productVariantId: itemsToAdd.at(-1)?.id,
      cartData: lastAddResponse,
    });

    if (startMarker) {
      globalThis.CartPerformance?.measureFromMarker?.('add:wait-for-subscribers', startMarker);
    }
  }

  /**
   * Render cart drawer
   */
  renderCartDrawer(cartDrawer, lastAddResponse) {
    if (!lastAddResponse.sections['cart-drawer']) {
      throw new Error('Error loading cart. Please try again.');
    }

    if (globalThis.CartPerformance?.measure) {
      globalThis.CartPerformance.measure('add:paint-updated-sections', () => {
        cartDrawer.renderContents(lastAddResponse);
      });
    } else {
      cartDrawer.renderContents(lastAddResponse);
    }
  }

  /**
   * Render cart notification
   */
  renderCartNotification(cartNotification, lastAddResponse) {
    const requiredSections = ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
    const missingSections = requiredSections.filter((id) => !lastAddResponse.sections[id]);

    if (missingSections.length > 0) {
      throw new Error('Error loading cart notification. Please try again.');
    }

    if (globalThis.CartPerformance?.measure) {
      globalThis.CartPerformance.measure('add:paint-updated-sections', () => {
        cartNotification.renderContents(lastAddResponse);
      });
    } else {
      cartNotification.renderContents(lastAddResponse);
    }
  }

  /**
   * Handle fallback to old cart URL method
   */
  handleCartFallback() {
    const cartParam = this.buildCartParam();
    const cartUrl = `https://${this.storeDomain}/cart/${cartParam}`;
    globalThis.location.href = cartUrl;
  }

  /**
   * Add a bucket to the items array if it has valid variant ID and quantity
   * @param {Object} bucket - The bucket to add
   * @param {Array} items - The items array to add to
   */
  addBucketToItems(bucket, items) {
    if (!bucket.variantId || bucket.count <= 0) return;

    const id = typeof bucket.variantId === 'string' ? bucket.variantId : String(bucket.variantId);
    items.push({
      id: id,
      quantity: bucket.count,
    });
  }

  /**
   * Process buckets from a breakdown and add them to items
   * @param {Object} bd - The breakdown to process
   * @param {Array} items - The items array to add to
   */
  processBreakdownBuckets(bd, items) {
    if (!bd.buckets || bd.buckets.length === 0) return;

    bd.buckets.forEach((bucket) => {
      this.addBucketToItems(bucket, items);
    });
  }

  /**
   * Check if brush-roller was already added from breakdowns
   * @returns {boolean} True if brush-roller is already in breakdowns with count > 0
   */
  isBrushRollerAdded() {
    return this.breakdowns.some((bd) => bd.handle === 'brush-roller' && bd.buckets?.some((b) => b.count > 0));
  }

  /**
   * Get the variant ID for the bonus product based on area
   * @param {number} area - The calculated area
   * @returns {string|null} The variant ID to add, or null if not available
   */
  getBonusVariantId(area) {
    const useBrush = area < this.bonusKitAreaThreshold;
    const currentBonusHandle = useBrush ? this.bonusBrushProductHandle : this.bonusBrushRollerProductHandle;
    const currentBonusVariantId = useBrush ? this.bonusBrushVariantId : this.bonusBrushRollerVariantId;

    // If variant ID is directly configured, use it
    if (currentBonusVariantId) {
      return String(currentBonusVariantId);
    }

    // Otherwise, find variant from product handle
    if (currentBonusHandle) {
      const bonusProduct = this.products.find((p) => p.handle === currentBonusHandle);
      if (bonusProduct?.variants?.length > 0) {
        const bonusVariant = bonusProduct.variants[0];
        if (bonusVariant?.variantId) {
          return typeof bonusVariant.variantId === 'string' ? bonusVariant.variantId : String(bonusVariant.variantId);
        }
      }
    }

    return null;
  }

  /**
   * Add bonus product to items if conditions are met
   * @param {number} area - The calculated area
   * @param {Array} items - The items array to add to
   */
  addBonusProductIfNeeded(area, items) {
    if (area <= 0 || this.isBrushRollerAdded()) return;

    const variantIdToAdd = this.getBonusVariantId(area);
    if (variantIdToAdd) {
      items.push({
        id: variantIdToAdd,
        quantity: 1, // Always add 1 bonus kit
      });
    }
  }

  getCartItems() {
    const items = [];
    const area = this.getArea();

    // 1. Process main product breakdowns (sealant, coating, primer, geo-textile, spray, etc.)
    this.breakdowns.forEach((bd) => {
      // Include visible products and brush-roller (if it's in breakdowns)
      if (!this.isVisible(bd.handle) && bd.handle !== 'brush-roller') return;

      // Process buckets for this breakdown
      this.processBreakdownBuckets(bd, items);
    });

    // 2. Add the BONUS product if area threshold is met and brush-roller not already added
    this.addBonusProductIfNeeded(area, items);

    return items;
  }

  async goToCheckout() {
    if (!this.validateCartItems()) {
      return;
    }

    const { cartDrawer, cartNotification } = this.getCartElements();

    if (!cartDrawer && !cartNotification) {
      this.handleCartFallback();
      return;
    }

    const ctaBtn = this.getCtaButton();
    this.setButtonLoading(ctaBtn, true);

    try {
      const itemsToAdd = this.getCartItems();

      if (itemsToAdd.length === 0) {
        this.showPopup('Please select at least 1 product');
        this.setButtonLoading(ctaBtn, false);
        return;
      }

      const cartElement = cartDrawer || cartNotification;
      if (cartElement) {
        cartElement.setActiveElement(ctaBtn || document.activeElement);
      }

      const lastAddResponse = await this.addAllItemsToCart(itemsToAdd, cartElement);

      // Update cart note with calculator context (length/area + source URL)
      await this.updateCartNoteFromCalculator();

      if (cartDrawer && !this.verifyCartDrawerResponse(lastAddResponse)) {
        this.showPopup('Error: No items found in cart. Please try again.');
        this.setButtonLoading(ctaBtn, false);
        return;
      }

      await this.publishCartUpdateEvent(itemsToAdd, lastAddResponse);

      if (cartDrawer) {
        this.renderCartDrawer(cartDrawer, lastAddResponse);
      } else if (cartNotification) {
        this.renderCartNotification(cartNotification, lastAddResponse);
      }
    } catch (error) {
      this.showPopup(error.message || 'Error adding items to cart. Please try again.');
    } finally {
      this.setButtonLoading(ctaBtn, false);
      cartDrawer?.classList.remove('is-empty');
    }
  }

  getFetchConfig() {
    // Use fetchConfig from global.js if available, otherwise use a compatible config
    if (typeof fetchConfig !== 'undefined') {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      // Delete Content-Type when using FormData - browser will set it with boundary
      delete config.headers['Content-Type'];
      return config;
    }
    return {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
  }

  showPopup(message) {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const popup = document.querySelector(`${sectionPrefix} #popup-modal-${sectionIdOnly}`);
    const messageEl = document.querySelector(`${sectionPrefix} #popup-message-${sectionIdOnly}`);

    if (popup && messageEl) {
      messageEl.textContent = message;
      popup.classList.remove('hidden');
    }
  }

  closePopup() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const popup = document.querySelector(`${sectionPrefix} #popup-modal-${sectionIdOnly}`);
    if (popup) {
      popup.classList.add('hidden');
    }
  }

  openQuickAddModal(productHandle, productId, openerElement) {
    // Construct product URL
    const productUrl = `/products/${productHandle}`;

    // Find or create quick-add-modal element
    let modal = document.querySelector(`#QuickAdd-${productId}`);

    if (!modal) {
      // Try to clone an existing modal structure first
      const existingModal = document.querySelector('quick-add-modal');
      if (existingModal) {
        modal = existingModal.cloneNode(true);
        modal.id = `QuickAdd-${productId}`;
        // Update the close button ID
        const closeBtn = modal.querySelector('[id^="ModalClose-"]');
        if (closeBtn) {
          closeBtn.id = `ModalClose-${productId}`;
        }
        // Update the info div ID
        const infoDiv = modal.querySelector('[id^="QuickAddInfo-"]');
        if (infoDiv) {
          infoDiv.id = `QuickAddInfo-${productId}`;
          infoDiv.innerHTML = ''; // Clear any existing content
        }
        document.body.appendChild(modal);
      } else {
        // No existing modal to clone - create from scratch
        // Get close icon HTML
        const closeIconHTML =
          '<span class="svg-wrapper"><svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M10 8.586L2.929 1.515 1.515 2.929 8.586 10l-7.071 7.071 1.414 1.414L10 11.414l7.071 7.071 1.414-1.414L11.414 10l7.071-7.071-1.414-1.414L10 8.586z" fill="currentColor"/></svg></span>';

        // Create using insertAdjacentHTML to avoid constructor timing issues
        const tempDiv = document.createElement('div');
        tempDiv.insertAdjacentHTML(
          'beforeend',
          `
          <quick-add-modal id="QuickAdd-${productId}" class="quick-add-modal">
            <div
              role="dialog"
              aria-label="Choose options for product"
              aria-modal="true"
              class="quick-add-modal__content global-settings-popup"
              tabindex="-1"
            >
              <button
                id="ModalClose-${productId}"
                type="button"
                class="quick-add-modal__toggle"
                aria-label="Close"
              >
                ${closeIconHTML}
              </button>
              <div id="QuickAddInfo-${productId}" class="quick-add-modal__content-info"></div>
            </div>
          </quick-add-modal>
        `
        );
        modal = tempDiv.querySelector('quick-add-modal');
        document.body.appendChild(modal);
      }
    }

    // Ensure opener element has data-product-url
    if (!openerElement.dataset.productUrl) {
      openerElement.dataset.productUrl = productUrl;
    }

    // Open the modal - openerElement should be a button with loading spinner
    if (modal && typeof modal.show === 'function') {
      modal.show(openerElement);
    }
  }
}

// Make it globally available
globalThis.KitBuilder = KitBuilder;
